import React, { useState, useEffect, useMemo, useRef } from "react";
import JSZip from "jszip";
import { 
  Layers as LayersIcon, 
  HelpCircle, 
  UploadCloud, 
  FileCheck, 
  CheckCircle2, 
  Cpu, 
  AlertCircle,
  TrendingUp,
  Boxes,
  ArrowRight,
  Info,
  X,
  FileText,
  CircuitBoard,
  Sliders,
  Search,
  BookOpen,
  Terminal,
  Check,
  Settings,
  RotateCcw,
  Maximize2,
  ZoomIn,
  ZoomOut
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";

// Definition for PCB order structure
interface PcbOrder {
  id: string;
  projectName: string;
  orderDate: string;
  baseMaterial: string;
  layers: number;
  width: number;
  height: number;
  qty: number;
  productType: string;
  thickness: number;
  color: string;
  silkscreen: string;
  materialType: string;
  surfaceFinish: string;
  copperWeight: string;
  innerCopperWeight: string;
  viaCovering: string;
  minViaSize: string;
  outlineTolerance: string;
  confirmFile: string;
  markOnPcb: string;
  testMethod: string;
  goldFingers: boolean;
  castellatedHoles: boolean;
  edgePlating: boolean;
  blindSlots: boolean;
  ulMarking: string;
  humidityCard: boolean;
  remark: string;
  fileName: string;
  fileSize: string;
  estimatedPrice: number;
  status: string;
}

const renderMaterialSvg = (materialName: string, sizeClass = "w-14 h-10 sm:w-16 sm:h-11") => {
  switch (materialName) {
    case "FR-4":
      return (
        <svg className={`${sizeClass} transition-transform duration-300 hover:scale-105`} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Main green fiberglass board substrate filling the dimensions */}
          <rect x="2" y="2" width="44" height="28" rx="4" fill="#047857" stroke="#065f46" strokeWidth="1.2" />
          {/* High-contrast gloss finish along the top edge */}
          <rect x="3" y="3" width="42" height="3" rx="1" fill="#34d399" opacity="0.35" />
          {/* Golden high-density trace routing */}
          <path d="M 6 15 h 14 l 4 -4 h 14" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          <path d="M 10 21 h 12 l 3 3 h 12" stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round" fill="none" />
          {/* Exposed solder mask pads (silver/golden vias) */}
          <circle cx="6" cy="15" r="1.8" fill="#fbbf24" stroke="#ffffff" strokeWidth="0.5" />
          <circle cx="20" cy="15" r="1" fill="#d97706" />
          <circle cx="34" cy="11" r="1.8" fill="#fbbf24" stroke="#ffffff" strokeWidth="0.5" />
          <circle cx="28" cy="24" r="1.5" fill="#fbbf24" stroke="#ffffff" strokeWidth="0.5" />
          {/* Surface Mount SMT Resistor */}
          <rect x="12" y="6" width="7" height="4" rx="0.5" fill="#1e293b" />
          <rect x="12" y="6" width="1.5" height="4" fill="#cbd5e1" />
          <rect x="17.5" y="6" width="1.5" height="4" fill="#cbd5e1" />
        </svg>
      );
    case "Flex":
      return (
        <svg className={`${sizeClass} transition-transform duration-300 hover:scale-105`} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Dynamic curved flexible Polyimide orange substrate */}
          <path d="M 2 11 Q 12 3, 24 15 T 46 11 L 46 21 Q 36 25, 24 13 T 2 21 Z" fill="#f97316" stroke="#c2410c" strokeWidth="1.2" />
          {/* Glossy flexible copper paths following curve */}
          <path d="M 4 14 Q 12 6, 24 18 T 44 14" stroke="#fbbf24" strokeWidth="1.3" strokeLinecap="round" fill="none" />
          <path d="M 4 18 Q 12 10, 24 22 T 44 18" stroke="#b45309" strokeWidth="1.2" opacity="0.85" strokeLinecap="round" fill="none" />
          {/* Flexible signal connector pads */}
          <circle cx="8" cy="13" r="1.8" fill="#fbbf24" stroke="#78350f" strokeWidth="0.5" />
          <circle cx="38" cy="13" r="1.8" fill="#fbbf24" stroke="#78350f" strokeWidth="0.5" />
        </svg>
      );
    case "Aluminum":
      return (
        <svg className={`${sizeClass} transition-transform duration-300 hover:scale-105`} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="metalGrad" x1="2" y1="22" x2="46" y2="22" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#94a3b8" />
              <stop offset="25%" stopColor="#f1f5f9" />
              <stop offset="50%" stopColor="#cbd5e1" />
              <stop offset="75%" stopColor="#f8fafc" />
              <stop offset="100%" stopColor="#94a3b8" />
            </linearGradient>
          </defs>
          {/* Bottom Brushed Aluminum base core plate */}
          <rect x="2" y="15" width="44" height="15" rx="2" fill="url(#metalGrad)" stroke="#64748b" strokeWidth="1" />
          {/* Top Dielectric white solder-mask insulated layer */}
          <rect x="2" y="2" width="44" height="12" rx="2" fill="#fafafa" stroke="#d1d5db" strokeWidth="1" />
          {/* High Dissipation LED footprints & traces */}
          <path d="M 6 8 h 12 M 30 8 h 12" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="24" cy="8" r="2.5" fill="#eab308" stroke="#374151" strokeWidth="0.5" />
          {/* Central heavy-duty thermal contact interface */}
          <rect x="22" y="10.5" width="4" height="4.5" fill="#64748b" />
        </svg>
      );
    case "Copper Core":
      return (
        <svg className={`${sizeClass} transition-transform duration-300 hover:scale-105`} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="copperGrad" x1="2" y1="22" x2="46" y2="22" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#7c2d12" />
              <stop offset="20%" stopColor="#ea580c" />
              <stop offset="50%" stopColor="#ffedd5" />
              <stop offset="80%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#7c2d12" />
            </linearGradient>
          </defs>
          {/* Solid Heavy Copper core heavy-current metal base plate */}
          <rect x="2" y="15" width="44" height="15" rx="3" fill="url(#copperGrad)" stroke="#7c2d12" strokeWidth="1.2" />
          {/* Top Premium heavy-spec brown substrate layer */}
          <rect x="2" y="2" width="44" height="12" rx="2" fill="#781d22" stroke="#451a03" strokeWidth="1" />
          {/* Thick copper electrical conductor tracks */}
          <path d="M 6 8 h 14 l 3 3 h 19" stroke="#ea580c" strokeWidth="2.2" strokeLinecap="round" fill="none" />
          <circle cx="6" cy="8" r="1.8" fill="#fbbf24" stroke="#ea580c" strokeWidth="0.5" />
          <circle cx="32" cy="11" r="1.8" fill="#fbbf24" stroke="#ea580c" strokeWidth="0.5" />
        </svg>
      );
    case "Rogers":
      return (
        <svg className={`${sizeClass} transition-transform duration-300 hover:scale-105`} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Ultra-low loss clean ceramic cream body */}
          <rect x="2" y="2" width="44" height="28" rx="4" fill="#fafaf9" stroke="#d6d3d1" strokeWidth="1.2" />
          {/* Microwave High-frequency Rogers stripline circuit line layout */}
          <path d="M 4 15 h 10 v -6 h 6 v 12 h 6 v -12 h 6 v 6 h 12" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          {/* Coaxial feed points and shield grounding vias to trace edges */}
          <circle cx="8" cy="23" r="1" fill="#78350f" />
          <circle cx="16" cy="23" r="1" fill="#78350f" />
          <circle cx="24" cy="23" r="1" fill="#78350f" />
          <circle cx="32" cy="23" r="1" fill="#78350f" />
          <circle cx="40" cy="23" r="1" fill="#78350f" />
          <circle cx="14" cy="7" r="1" fill="#78350f" />
          <circle cx="28" cy="7" r="1" fill="#78350f" />
        </svg>
      );
    case "PTFE Teflon":
      return (
        <svg className={`${sizeClass} transition-transform duration-300 hover:scale-105`} viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* High thermal / Chemical stability black PTFE Teflon matrix */}
          <rect x="2" y="2" width="44" height="28" rx="4" fill="#0f172a" stroke="#334155" strokeWidth="1.2" />
          {/* Woven glass fabric structure visual textures */}
          <path d="M2 7 h44 M2 13 h44 M2 19 h44 M2 25 h44 M8 2 v28 M16 2 v28 M24 2 v28 M32 2 v28 M40 2 v28" stroke="#1e293b" strokeWidth="0.6" opacity="0.65" />
          {/* Golden spiral RF loop tracking */}
          <path d="M 6 11 Q 12 5, 24 14 T 42 11" stroke="#fbbf24" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.95" />
          <circle cx="6" cy="11" r="1.8" fill="#fbbf24" stroke="#ffffff" strokeWidth="0.5" />
          <circle cx="42" cy="11" r="1.8" fill="#fbbf24" stroke="#ffffff" strokeWidth="0.5" />
        </svg>
      );
    default:
      return null;
  }
};

const renderProductTypeSvg = (type: string) => {
  switch (type) {
    case "Industrial/Consumer electronics":
      return (
        <svg className="w-16 h-12 mx-auto mb-2 transition-transform duration-300 hover:scale-105" viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="6" width="56" height="36" rx="6" fill="#1e293b" stroke="#475569" strokeWidth="1.5" />
          <rect x="18" y="14" width="14" height="14" rx="2" fill="#0f172a" stroke="#38bdf8" strokeWidth="1.5" />
          <path d="M12 16 H18 M12 21 H18 M12 26 H18" stroke="#38bdf8" strokeWidth="1" />
          <path d="M32 16 H38 M32 21 H38 M32 26 H38" stroke="#38bdf8" strokeWidth="1" />
          <path d="M21 10 V14 M25 10 V14 M29 10 V14" stroke="#38bdf8" strokeWidth="1" />
          <rect x="42" y="14" width="6" height="10" rx="1" fill="#ea580c" />
          <rect x="43" y="14" width="4" height="2" fill="#e2e8f0" />
          <rect x="43" y="22" width="4" height="2" fill="#e2e8f0" />
          <path d="M15 36 H30 L35 31 H48" stroke="#10b981" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      );
    case "Aerospace":
      return (
        <svg className="w-16 h-12 mx-auto mb-2 transition-transform duration-300 hover:scale-105" viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="6" width="56" height="36" rx="6" fill="#020617" stroke="#1e1b4b" strokeWidth="1.5" />
          <path d="M24 24 L40 24 M32 16 L32 32" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" />
          <rect x="16" y="20" width="8" height="8" rx="1" fill="#38bdf8" stroke="#0284c7" strokeWidth="1" />
          <rect x="40" y="20" width="8" height="8" rx="1" fill="#38bdf8" stroke="#0284c7" strokeWidth="1" />
          <circle cx="32" cy="24" r="5" fill="#e2e8f0" stroke="#94a3b8" />
          <circle cx="12" cy="14" r="1" fill="#ffffff" />
          <circle cx="50" cy="12" r="1.5" fill="#fbbf24" />
          <circle cx="48" cy="34" r="1" fill="#ffffff" />
          <path d="M10 32 Q20 36 30 32" stroke="#4f46e5" strokeWidth="1.2" opacity="0.6" strokeLinecap="round" />
        </svg>
      );
    case "Medical":
      return (
        <svg className="w-16 h-12 mx-auto mb-2 transition-transform duration-300 hover:scale-105" viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="6" width="56" height="36" rx="6" fill="#f0fdf4" stroke="#bbf7d0" strokeWidth="1.5" />
          <path d="M16 16 H24 V8 H32 V16 H40 V24 H32 V32 H24 V24 H16 Z" fill="#ef4444" />
          <path d="M12 30 H26 L29 20 L33 38 L37 28 L40 32 H52" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <circle cx="12" cy="30" r="1.5" fill="#ef4444" />
          <circle cx="52" cy="30" r="1.5" fill="#ef4444" />
        </svg>
      );
    default:
      return null;
  }
};

const t = {
  // Main headings
  baseSpecs: { fa: "مشخصات پایه برد:", en: "Base Specs & Substrate:" },
  baseSpecsDesc: { fa: "", en: "These options directly impact the base fabrication cost of the blank PCB." },
  pcbSpecs: { fa: "جدول مشخصات اختصاصی:", en: "PCB Custom Specifications:" },
  pcbSpecsDesc: { fa: "فرمت تحویل، ضخامت لایه‌مس و مشخصات تکمیلی بدنه", en: "Delivery formats, board thickness, masks, and surface finish options." },
  highSpecs: { fa: "مشخصات اختصاصی برد:", en: "Custom Specifications & Advanced Options:" },
  highSpecsDesc: { fa: "", en: "Advanced process parameters: gold fingers, castellated routing, outline margins and safety markings." },
  remarkLabel: { fa: "یادداشت فنی یا توضیحات ویژه:", en: "Technical Remark / Custom Request Note:" },
  remarkDesc: { fa: "امپدانس خاص سیگنال فرکانسی، کنترلر امپدانس یا درخواست لایه خاص را یادداشت کنید.", en: "Note custom impedance values (e.g. 50Ω RF line matching), layer stackups, or specialized router constraints." },
  remarkPlaceholder: { fa: "مثال: ضخامت مس ۲ اونس حتما اعمال شود. امپدانس ۵۰ اهم بر روی لاین‌های فرکانس بالا (RF) طبق گربر رعایت گردد.", en: "e.g., Maintain 50-ohm impedance on microstrip tracks on layer 1. 2oz outer copper thickness requested." },
  
  // Gerber upload section
  gerberTitle: { fa: "فایل ساخت گربر:", en: "CAD Layout & Gerber File Upload:" },
  gerberDesc: { fa: "فایل‌های معتبر ZIP یا RAR شامل لایه مس، سولدر، چاپ راهنما و خطوط برش اوت‌لاین.", en: "Uploaded archives should contain Gerber RS-274X details with drill data (NC Drill file) in a ZIP/RAR." },
  gerberUploadBtn: { fa: "کشیدن و رها کردن فایل گربر (یا کلیک کنید)", en: "Drag & drop Gerber files here (or click to browse)" },
  gerberHelpText: { fa: "ZIP / RAR (Silkscreen, Solder, Copper, Drill, Outline)", en: "ZIP / RAR (Silkscreen, Solder, Copper, Drill, Outline)" },
  gerberUploading: { fa: "در حال آنالیز و آپلود فایل گربر...", en: "Parsing Gerber files & checking drill coordinates..." },
  gerberFileSize: { fa: "حجم فایل: ", en: "File size: " },
  gerberEdit: { fa: " - برای ویرایش کلیک کنید", en: " - Click to change files" },
  gerberRequiredMarker: { fa: "الزامی", en: "Required" },
  
  // Quote Receipt
  liveQuote: { fa: "پیش‌فاکتور زنده ساخت برد:", en: "Live Fabrication Invoice & Quote" },
  liveQuoteDesc: { fa: "قیمت نهایی سفارش بر اساس متریال انتخابی، تعداد لایه‌ها و پارت‌های تولیدی محاسبه شده است.", en: "Instant estimation computed from base material, layer stack, physical dimensions, and volume specs." },
  liveUpdateBadge: { fa: "بروزرسانی انحصاری زنده", en: "Live Sync Active" },
  summaryMat: { fa: "متریال و لایه‌ها:", en: "Material & Stack:" },
  summaryDim: { fa: "ابعاد و تعداد نهایی:", en: "Dimensions & Qty:" },
  summaryColor: { fa: "رنگ و ساختار نهایی پدها:", en: "Color & Finish Pad:" },
  summaryNumLayers: { fa: "لایه", en: "Layers" },
  summaryUnits: { fa: "عدد", en: "pcs" },
  
  priceBase: { fa: "تعرفه پایه شیت:", en: "Base Board Charge:" },
  priceLayers: { fa: "لایه‌های متالایز:", en: "Plated Layers Addition:" },
  priceSize: { fa: "ابعاد مازاد طول/عرض:", en: "Oversize Multiplier:" },
  priceQty: { fa: "تعداد درخواستی:", en: "Volume Scale Cost:" },
  priceMatCost: { fa: "اضافه‌بهای آلیاژ:", en: "Specialty Alloy Surcharge:" },
  priceFinish: { fa: "آبکاری پدهای برد:", en: "Surface Pad Finish:" },
  priceHighSpecs: { fa: "الحاق فنی موارد خاص:", en: "Precision Spec Add-ons:" },
  priceTotalLabel: { fa: "مجموع کل برآورد هزینه ساخت", en: "Estimated Fabrication Subtotal:" },
  priceTomanApprox: { fa: "معادل تقریبی", en: "Approx. equivalent of" },
  priceTomanApproxSuffix: { fa: "تومان", en: "Toman" },
  
  submitBtn: { fa: "ثبت نهایی و ذخیره سفارش", en: "Submit & Save PCB Order" },
  
  // Field names
  projectName: { fa: "نام پروژه:", en: "Project Name:" },
  pcbType: { fa: "نوع برد:", en: "PCB Type:" },
  boardSpec: { fa: "استاندارد برد:", en: "BoardSpec:" },
  baseMaterial: { fa: "جنس پایه:", en: "Base Material:" },
  layers: { fa: "تعداد لایه‌ها:", en: "Layers Stackup:" },
  dimensions: { fa: "ابعاد برد:", en: "Board Dimensions:" },
  qty: { fa: "تعداد برد:", en: "PCB Quantity:" },
  color: { fa: "رنگ برد:", en: "PCB Color Solder Mask:" },
  differentDesign: { fa: "تنوع طراحی در پنل:", en: "Different Designs in Panel:" },
  deliveryFormat: { fa: "چیدمان:", en: "Delivery Format:" },
  thickness: { fa: "ضخامت نهایی:", en: "Final Thickness:" },
  surfaceFinish: { fa: "پوشش نهایی پدها:", en: "Surface Finish:" },
  materialType: { fa: "جنس برد:", en: "Substrate Grade (TG):" },
  silkscreen: { fa: "چاپ راهنما:", en: "Silkscreen Layer (Labels):" },
  copperWeight: { fa: "ضخامت مس خارجی:", en: "Outer Copper Weight:" },
  innerCopperWeight: { fa: "ضخامت داخلی مس:", en: "Inner Copper Weight:" },
  viaCovering: { fa: "پوشش سوراخ‌ها:", en: "Via Protection Layer:" },
  viaPlating: { fa: "روش آبکاری سوراخ‌ها:", en: "Via Plating Method:" },
  minViaSize: { fa: "حداقل قطر سوراخ / پد:", en: "Minimum Via Drill/Pad:" },
  outlineTolerance: { fa: "تولرانس دور برد:", en: "Board Tolerance Margin:" },
  confirmFile: { fa: "تایید فایل پیش از تولید:", en: "Preforce Blueprint Verify:" },
  goldFingers: { fa: "درگاه انگشتی طلا:", en: "Gold Edge Contact (Fingers):" },
  castellatedHoles: { fa: "سوراخ قلعه‌ای:", en: "Castellated Edge Plating:" },
  edgePlating: { fa: "آبکاری لبه فیبر:", en: "Outside Edge Metalization:" },
  blindSlots: { fa: "شیار کور کات:", en: "Blind Slot Routing:" },
  markOnPcb: { fa: "مارک روی برد:", en: "PCB Vendor Tracking Mark:" },
  ulMarking: { fa: "استاندارد UL:", en: "Flame Resistor UL Mark:" },
  humidityCard: { fa: "کارت رطوبت‌سنج در بسته‌بندی:", en: "Moisture Card in Pack:" }
};

const helpData = {
  projectName: {
    title: { fa: "نام پروژه", en: "Project Name" },
    content: {
      fa: "نام منحصربه‌فرد یا شناسه قطعه برای طرح خود وارد کنید. این نام به آرشیو فایل گربر الصاق شده و به رهگیری برد پس از تولید نهایی کمک زیادی می‌کند.",
      en: "Enter a descriptive name or custom part number for your circuit board. This helps locate your order in our database and tracks physical hardware during final assembly."
    }
  },
  pcbType: {
    title: { fa: "نوع برد", en: "PCB Type" },
    content: {
      fa: "بردهای با تراکم اتصالات بالا (HDI) شامل وایاهای کور و/یا مدفون هستند و اغلب دارای وایاهای میکرو با قطر ۰.۰۰۶ یا کمتر در قطر می‌باشند. آن‌ها چگالی مداری بالاتری نسبت به بردهای مدار سنتی دارند.",
      en: "High density interconnects (HDI) Boards contain blind and/or buried vias and often contain micro vias of .006 or less in diameter. They have a higher circuitry density than traditional circuit boards."
    }
  },
  boardSpec: {
    title: { fa: "استاندارد برد", en: "Board Spec" },
    content: {
      fa: "استانداردها برای تولید بردهای مدار چاپی الزامی هستند.",
      en: "Standards are required to produce PCBs"
    }
  },
  baseMaterial: {
    title: { fa: "جنس لایه پایه (Base Material)", en: "Base Substrate Alloy" },
    content: {
      fa: "FR4 رایج‌ترین ماده دی‌الکتریک درجه یک است که در ساخت بردهای مدار چاپی استفاده می‌شود.\nبردهای آلومینیومی نسبت به ساختارهای استاندارد FR-4 دفع حرارت و انتقال حرارتی بهتری دارند.\nبردهای با پایه مس رسانایی حرارتی بسیار خوبی دارند که حتی بهتر از بردهای آلومینیومی است.\nبه دلیل ویژگی تلفات دی‌الکتریک پایین، روجرز اغلب در بردهای فرکانس بالا استفاده می‌شود.\nاگر به مواد دیگری نیاز دارید، لطفا با نماینده فروش خود تماس بگیرید.",
      en: "FR4 is the most common arade dielectric material that is used in the fabrication of circu boards.\nAluminum boards have better heat dissipation and thermal transfer than standard FR-4 constructions.\nThe copper base boards have a great thermal conductivity better than aluminum PCB.\nDue to its characteristics of low dielectric loss, Rogers is often used in high-frequency PCB. \nIf you need other materials, please contact your sales representative"
    }
  },
  layers: {
    title: { fa: "تعداد لایه‌های هادی مس", en: "Copper Layer Stack" },
    content: {
      fa: "تعداد لایه‌های مس در برد. در حال حاضر امکان تولید تا ۶۰ لایه وجود دارد، اگر به بیش از ۱۴ لایه نیاز دارید، لطفا صفحه پیشرفته تولید PCB را جهت قیمت‌گذاری انتخاب کنید.",
      en: "The number of copper layers in the board. PCBWay can currently produce up to 60 layers, if you need more than 14 layers, please select the advanced PCB page for valuation."
    }
  },
  dimensions: {
    title: { fa: "ابعاد فیزیکی کیت (Dimensions)", en: "Board Dimensions & Size" },
    content: {
      fa: "اندازه برد به طول و عرض برد سفارش داده شده توسط مشتری اشاره دارد. اگر برد دایره‌ای باشد، طول و عرض همان قطر دایره است؛ اگر برد با شکل نامنظم باشد، طول و عرض بر اساس بزرگترین محدوده شکل اندازه‌گیری می‌شود.",
      en: "The board size refers to the length and width of the board ordered by the customer. If it is a circular board, the length and width are the diameter of the circle; If it is a board with irregular shape, the length and width are measured according to the largest shape range."
    }
  },
  qty: {
    title: { fa: "تعداد سفارش (PCB Qty)", en: "Purchase Volume (Qty)" },
    content: {
      fa: "لطفاً تعداد قطعات تکی مورد نیاز خود را انتخاب کنید. برای بردهای استاندارد، حداقل تعداد سفارش ۱ عدد می‌باشد.",
      en: "Please choose the number of single pieces you need. For standard PCB, the starting quantity is 1 pieces."
    }
  },
  color: {
    title: { fa: "رنگ چاپ محافظ سولدر ماسک", en: "Solder Mask Solder Resist Color" },
    content: {
      fa: "رنگ سولدر ماسک به رنگ سطح برد چاپی اشاره دارد. در حال حاضر ۹ رنگ متداول سولدرماسک ارائه می‌شود. اگر به رنگ صورتی، خاکستری، نارنجی یا شفاف نیاز دارید، لطفا به بخش سفارش پیشرفته مراجعه کنید.",
      en: "Solder mask color refers to the color of the PCB surface. Currently, PCBWay provides 9 conventional solder mask colors. If you need pink, gray, orange, or transparent, please go to the Advanced PCB page to choose."
    }
  },
  differentDesign: {
    title: { fa: "تنوع طرح مجزا در پنل (Different Design)", en: "Paneled Designs Count" },
    content: {
      fa: "این بدان معنی است که طرح‌های متفاوتی در فایل‌های شما وجود دارد که با وی‌کات، سوراخ جفت (تمبر)، شیارهای فرز یا موارد دیگر تفکیک شده‌اند. در صورتی که انواع مختلفی از طراحی‌های برد چاپی در یک فایل گربر وجود داشته باشد، هزینه اضافی پنل محاسبه خواهد شد.",
      en: "It means there are different designs in your files separated by v-cuts, stamp holes, milling slots or others. We willl charge additional panel cost if there are different kinds of PCB designs in one Gerber file."
    }
  },
  deliveryFormat: {
    title: { fa: "چگونگی بسته‌بندی برش (Delivery Format)", en: "Delivery & Routing Format" },
    content: {
      fa: "بردهای مدار چاپی به صورت قطعات تکی یا پنلی ارسال می‌شوند. برای سفارشات مونتاژ انبوه (تعداد بیشتر از ۳۰)، توصیه می‌کنیم به صورت پنل ارسال شوند که این کار راندمان مونتاژ را بهبود می‌دهد.",
      en: "PCBs will be shipped in single pieces or in panel. For bulk assembly orders (quantity greater than 30), we recommend shipping in panel, which will improve the assembly efficiency."
    }
  },
  thickness: {
    title: { fa: "ضخامت برد نهایی (Thickness)", en: "Finished Board Thickness" },
    content: {
      fa: "برای مواد عادی، ضخامت برد به ضخامت نهایی مدار چاپی پس از تولید اشاره دارد؛ درصد خطا حدود ۱۰ درصد (+/-10% (T>=1mm) یا +/- 0.1mm (T<1mm)) خواهد بود. این رواداری عمدتاً به دلیل سولدر ماسک و فرآیند مس‌نشانی پدید می‌آید. اگر الزامات دیگری برای خطای ضخامت دارید، لطفاً در بخش یادداشت ویژه درج کنید. توجه: برای بردهای روجرز یک‌رو یا دورو، ضخامت به ضخامت بدنه پایه (آلیاژ) اشاره دارد.",
      en: "For ordinary materials, the board thickness refers to the thickness of the PCB after production is complete, and the error is about 10% (+/-10% (T>=1mm) or +/- 0.1mm (T<1mm).). The error is mainly caused by solder mask and copper sinking.\nIf there are other requirements for thickness error, please make a note in Other Special Requirements. Note: For single sided/double sided Rogers board, the thickness refers to thickness of substrate."
    }
  },
  surfaceFinish: {
    title: { fa: "نوع پوشش متالیزه پدها (Surface Finish)", en: "Surface Metallurgy (HASL/ENIG)" },
    content: {
      fa: "پوشش نهایی پدها بخش مهمی از تولید برد چاپی است. این پوشش از مس در برابر اکسیداسیون که سبب غیرقابل استفاده شدن برد می‌شود، محافظت می‌کند. همچنین سطحی بسیار هموار برای لحیم‌کاری آسان قطعات فراهم می‌نماید.",
      en: "Surface finish is an important part of PCB production. It can protect the copper from oxidation, which would render the PCB unusable. In addition, it provides a smooth surface for the components to be soldered."
    }
  },
  materialType: {
    title: { fa: "نوع تحمل دمایی بدنه (Material Type / TG)", en: "Substrate Temperature Grade (TG)" },
    content: {
      fa: "FR4 رایج‌ترین ماده دی‌الکتریک درجه یک است که در ساخت بردهای مدار چاپی استفاده می‌شود.\nبردهای آلومینیومی نسبت به ساختارهای استاندارد FR-4 دفع حرارت و انتقال حرارتی بهتری دارند.\nبردهای با پایه مس رسانایی حرارتی بسیار خوبی دارند که حتی بهتر از بردهای آلومینیومی است.\nبه دلیل ویژگی تلفات دی‌الکتریک پایین، روجرز اغلب در بردهای فرکانس بالا استفاده می‌شود.\nاگر به مواد دیگری نیاز دارید، لطفا با نماینده فروش خود تماس بگیرید.",
      en: "FR4 is the most common arade dielectric material that is used in the fabrication of circu boards.\nAluminum boards have better heat dissipation and thermal transfer than standard FR-4 constructions.\nThe copper base boards have a great thermal conductivity better than aluminum PCB.\nDue to its characteristics of low dielectric loss, Rogers is often used in high-frequency PCB.\nIf you need other materials, please contact your sales representative."
    }
  },
  silkscreen: {
    title: { fa: "رنگ چاپ راهنمای مونتاژ (Silkscreen)", en: "Silkscreen Text Color" },
    content: {
      fa: "چاپ راهنما (سایه‌نما) لایه‌ای از جوهر است که برای شناسایی قطعات برد، نشانه‌گذاری‌ها، لوگوها، نمادها و غیره استفاده می‌شود. رنگ آن معمولاً سفید است. اما زمانی که سطح سولدر ماسک سفید باشد، چاپ روی آن مشکی خواهد شد.",
      en: "The silkscreen is a layer of ink trace used to identify the PCB components, marks, logos, symbols, and so on. The color is usually white. But when the solder mask ink is white, the silkscreen will be black."
    }
  },
  copperWeight: {
    title: { fa: "ضخامت مس خارجی (Outer Copper Weight)", en: "Outer Copper Layer Foil Weight" },
    content: {
      fa: "وزن مس بر حسب اونس در یک فوت مربع برد مدار چاپی؛ این گزینه ضخامت مس در لایه‌های بیرونی را تعیین می‌کند. ضخامت‌های مس ۱ تا ۲۰ اونس قابل ارائه است. سوراخ‌های متالیزه یا وایاهایی که بریده می‌شوند تا یک سوراخ نیمه ایجاد کنند و دهانه‌ای در دیواره سوراخ تشکیل دهند. معمولاً آنها برای نصب یک برد به بردی دیگر استفاده می‌شوند.",
      en: "The weight in ounces of copper in one square foot of PCB, this option will determine the thickness of copper on the outer layers. PCBWay provides the thickness of copper on outer layers of 1-20oz.PTH holes or vias that are cutted through to create a partial or half hole to form an opening into the side of the hole barrel. Generally they are used for mounting a PCB to another one."
    }
  },
  innerCopperWeight: {
    title: { fa: "ضخامت مس داخلی (Inner Copper Weight)", en: "Inner Copper Layer Foil Weight" },
    content: {
      fa: "وزن مس بر حسب اونس در یک فوت مربع برد مدار چاپی؛ این گزینه ضخامت مس در لایه‌های داخلی را تعیین می‌کند. ضخامت‌های مس ۱ تا ۲۰ اونس قابل ارائه است. سوراخ‌های متالیزه یا وایاهایی که بریده می‌شوند تا یک سوراخ نیمه ایجاد کنند و دهانه‌ای در دیواره سوراخ تشکیل دهند. معمولاً آنها برای نصب یک برد به بردی دیگر استفاده می‌شوند.",
      en: "The weight in ounces of copper in one square foot of PCB, this option will determine the thickness of copper on the outer layers. PCBWay provides the thickness of copper on outer layers of 1-20oz.PTH holes or vias that are cutted through to create a partial or half hole to form an opening into the side of the hole barrel. Generally they are used for mounting a PCB to another one"
    }
  },
  viaCovering: {
    title: { fa: "پوشش محافظ سوراخ‌ها (Via Covering)", en: "Via Pocket Protection" },
    content: {
      fa: "چگونگی ایزوله‌سازی سوراخ‌های میانی برد. به با سولدرماسک پوشاندن کامل که ایزولاسیون بالایی دارد Tented گویند. باز و لخت گذاشتن Untented و مسدود سازی عایق سوراخ‌ها Plugged نامیده می‌شود.",
      en: "Dictates protective lacquer flow over vias: 'Tented' covers via holes to prevent bare exposure. 'Untented' leaves vias open for active probe diagnostics. 'Plugged' fills vias with epoxy formulation for thermal/signal shielding."
    }
  },
  viaPlating: {
    title: { fa: "آبکاری رسانایی سوراخ‌ها (Via Plating)", en: "Internal Via Cu-Plating Method" },
    content: {
      fa: "روش ویژه قرار دادن متالیزه رسانا داخل دیواره راهگاه سیگنال‌های ریز؛ به ویژه روش Selective یا رزین رسانا به منظور پایداری بهتر سیگنال‌های فاقد اعوجاج فرکانسی.",
      en: "The specialized treatment inside laser-drilled communication tunnels. Selectively filling holes with conductive silver/copper compound or horizontal chemical copper deposition ensures zero micro-voids."
    }
  },
  minViaSize: {
    title: { fa: "حداقل اندازه سوراخ متالیزه (Min Via Size)", en: "Minimum Via & Pad Size Tolerances" },
    content: {
      fa: "کوچک‌ترین ابعاد مته‌زنی سوراخ انتقال یا حلقه دور هادی. ابعاد کوچک‌تر از 0.3mm به ماشین‌کاری دقیق لیزری متوسل شده و هزینه ثانویه دارد.",
      en: "The smallest drill diameter allowed for layer-to-layer connections. Narrow apertures (0.2mm or below) require specialized micro-drills and meticulous chemical desmearing cycles, adding to price indices."
    }
  },
  outlineTolerance: {
    title: { fa: "رواداری خطای لبه برد (Outline Tolerance)", en: "Routing Outline Edge Tolerance" },
    content: {
      fa: "تولرانس و لقی برش فرزکاری لبه مدار. برای بردهای داخل باکس محفظه فلزی بسیار فشرده، و تولرانس 0.1± میلیمتری از چفت شدن آسان سیستم پشتیبانی می‌کند.",
      en: "The physical dimensional variance tolerated at the routed PCB edges. Standard is ±0.2mm. Tightly tolerance enclosure components or sliding-rail connectors request High Precision (±0.1mm)."
    }
  },
  confirmFile: {
    title: { fa: "تایید فایل پیش از ساخت انبوه", en: "Engineer Pre-Fab File Approval" },
    content: {
      fa: "بررسی نهایی لایه‌ها قبل از رفتن به مرحله خط تولید. در صورت لزوم نقشه تحلیل شده کارگاه برای شما و ایمیل جهت تایید مجدد ارسال شده و سفارش ایمن‌تر ثبت می‌شود.",
      en: "If chosen, fab house engineers generate visual blueprint previews and wait for your email/web sign-off check prior to actual mass panel imaging and chemical etching."
    }
  },
  goldFingers: {
    title: { fa: "کانکتور انگشتی طلا (Gold Fingers)", en: "Hard Gold Electroplated Fingers" },
    content: {
      fa: "آبکاری با ضخامت طلای سخت بر روی پدها و شانه‌های کانکتور لبه برد. جهت اتصال به اسلات‌های کارت‌های توسعه و پی‌سی‌آی اکسپرس برای سایش دائم و مکرر فلز.",
      en: "Heavy hard-gold plating applied over edge connection terminals (like PCIe expanders). Resists frictional wear-and-tear under recurrent slot insertions, guaranteeing solid long-term electrical connections."
    }
  },
  castellatedHoles: {
    title: { fa: "پدهای سوراخ قلعه‌ای کناری", en: "Castellated Edge Grooves" },
    content: {
      fa: "سوراخ‌های متالیزه شده‌ای که دقیقا روی لبه نهایی برد تراش می‌خورند تا یک برد فرعی کوچک را بتوان مثل یک ماژول هدر تخت، به صورت SMD روی برد مادر اصلی لحیم کاری کرد.",
      en: "Plated holes split in half mechanically on the board boundary. This forms gold-plated castellations, allowing the module to be easily flat-soldered onto other PCBs as a Surface-mount IC."
    }
  },
  edgePlating: {
    title: { fa: "آبکاری فلزی بیرونی لبه‌ها (Edge Plating)", en: "Outer Side-Edge Plating Wrap" },
    content: {
      fa: "پوشش لبه‌های پهلویی بیرونی مدار چاپی با آلیاژ رسانا. برای کاهش نویز EMI مدارهای فرکانس بالا و اتصال زمین یکپارچه دور مدار کارایی دارد.",
      en: "Wraps outer physical board vertical edges in metalized corporate copper. Improves heat cooling surface, provides EMI grounding shield, and connects top-to-bottom ground ring shields easily."
    }
  },
  blindSlots: {
    title: { fa: "شکاف و شیارهای هدایت شده کور (Blind)", en: "Blind & Buried Slots" },
    content: {
      fa: "شکاف مکانیکی برش عمقی غیرنافذ در لایه‌های میانی یا تراش با ماشین‌کاری عمودی جهت خمش کابل یا ساخت کانکتورها و حفره‌های قرارگیری قطعات.",
      en: "Controlled-depth partial-routed slots and internal routing tracks that do not pass through both outer layers, aiding high-end modular stacks and precision cavities."
    }
  },
  markOnPcb: {
    title: { fa: "لوگو و شناسه پیگیری کارخانه", en: "Vendor Tracking Serialization" },
    content: {
      fa: "کد پیگیری کوچک چاپ محافظ کارخانه برای ثبت سفارش در خط مونتاژ. برای داشتن مداری فاقد حروف متفرقه تزیینی کارگاه گزینه Remove Mark با هزینه اندکی این نشان را برمی‌دارد.",
      en: "A small tracking code printed selectively by the fab house line to track bare boards. Selecting 'Remove Mark' prompts engineers to erase this numbering layer for a pure custom appearance."
    }
  },
  ulMarking: {
    title: { fa: "درج نماد استاندارد ضد حریق UL", en: "UL Flammability Mark" },
    content: {
      fa: "چاپ نشان گواهی کیفیت ایمنی نسوز شرکت UL در برابر دمای شدید. الزامی گمرکی جهت اخذ تاییدیه‌های استاندارد فروش در کشورهای آمریکایی و اروپایی.",
      en: "Prints the Underwriters Laboratories safety stamp on your solder mask, signifying optimal flammability rating compliance of the fiberglass matrix for consumer sales certifications."
    }
  },
  humidityCard: {
    title: { fa: "کارت معرف میزان رطوبت داخل جعبه", en: "Environmental Humidity Card" },
    content: {
      fa: "قرارگیری یک قطعه شناساگر حساس شیمیایی در پکیج ضدرطوبت وکیوم برد. تضمین اینکه در گمرک یا آفر حمل، رطوبت هوا به چیدمان پدها آسیب نرسانده است.",
      en: "Adds a moisture indication card inside your vacuum sealed static-shield bag, flashing indicators if humidity is encountered, verifying safe dry pads before oven assembly."
    }
  }
};

const colorNames: Record<string, { fa: string; en: string }> = {
  Green: { fa: "سبز", en: "Green" },
  Red: { fa: "قرمز", en: "Red" },
  Yellow: { fa: "زرد", en: "Yellow" },
  Blue: { fa: "آبی", en: "Blue" },
  White: { fa: "سفید", en: "White" },
  Black: { fa: "مشکی", en: "Black" },
  Pink: { fa: "صورتی", en: "Pink" },
  Grey: { fa: "خاکستری", en: "Grey" },
  Orange: { fa: "نارنجی", en: "Orange" },
  Transparent: { fa: "شفاف", en: "Transparent" },
  Purple: { fa: "بنفش", en: "Purple" },
  "Matte black": { fa: "مشکی مات", en: "Matte Black" },
  "Matte green": { fa: "سبز مات", en: "Matte Green" },
  "Matte blue": { fa: "آبی مات", en: "Matte Blue" },
  "Matte red": { fa: "قرمز مات", en: "Matte Red" },
  None: { fa: "ندارد", en: "None" }
};

const silkscreenNames: Record<string, { fa: string; en: string }> = {
  White: { fa: "سفید", en: "White" },
  Black: { fa: "مشکی", en: "Black" },
  Yellow: { fa: "زرد", en: "Yellow" },
  Blue: { fa: "آبی", en: "Blue" },
  Grey: { fa: "خاکستری", en: "Grey" },
  None: { fa: "ندارد", en: "None" }
};

interface ExtensionInfo {
  ext: string;
  category: string;
  name: { fa: string; en: string };
  desc: { fa: string; en: string };
  altiumMeaning: string;
}

const buildRecognizedExtensionsList = (): ExtensionInfo[] => {
  const list: ExtensionInfo[] = [
    {
      ext: "GTL",
      category: "copper",
      name: { fa: "مس لایه رو (Top Copper)", en: "Top Copper Layer" },
      desc: { fa: "طرح خطوط رسانا، مسیرها و پدهای SMD بالا.", en: "Top conductor routing pattern and SMT pads." },
      altiumMeaning: "Top Layer Gerber Data"
    },
    {
      ext: "GBL",
      category: "copper",
      name: { fa: "مس لایه زیر (Bottom Copper)", en: "Bottom Copper Layer" },
      desc: { fa: "طرح خطوط رسانا و ترک‌های لایه بیرونی پایین.", en: "Bottom copper trace and routing layer." },
      altiumMeaning: "Bottom Layer Gerber Data"
    },
    {
      ext: "GTS",
      category: "mask",
      name: { fa: "چاپ محافظ لایه رو (Top Solder Mask)", en: "Top Solder Mask Layer" },
      desc: { fa: "رنگ محافظ سولدر جهت جلوگیری از اتصال قلع هنگام مونتاژ در لایه رو.", en: "Soldermask layer opening defining SMT pin solder points." },
      altiumMeaning: "Top Solder Mask Gerber Data"
    },
    {
      ext: "GBS",
      category: "mask",
      name: { fa: "چاپ محافظ لایه زیر (Bottom Solder Mask)", en: "Bottom Solder Mask Layer" },
      desc: { fa: "سولدرماسک محافظ اتصالات برای ممانعت از پخش قلع زیر بورد.", en: "Soldermask resist openings on bottom copper pads." },
      altiumMeaning: "Bottom Solder Mask Gerber Data"
    },
    {
      ext: "GTO",
      category: "silk",
      name: { fa: "چاپ راهنمای رو (Top Silkscreen)", en: "Top Silkscreen Layer" },
      desc: { fa: "نوشته‌ها، مقادیر، کادربندی قطعات و لوگوی شرکت روی بورد.", en: "Top component designators, values, and visual borders." },
      altiumMeaning: "Top Overlay Gerber Data"
    },
    {
      ext: "GBO",
      category: "silk",
      name: { fa: "چاپ راهنمای زیر (Bottom Silkscreen)", en: "Bottom Silkscreen Layer" },
      desc: { fa: "چاپ سفید مراجع قطعات لایه پایین بورد جهت سهولت عیب‌یابی.", en: "Bottom side component designators and placement labels." },
      altiumMeaning: "Bottom Overlay Gerber Data"
    },
    {
      ext: "GTP",
      category: "paste",
      name: { fa: "شابلون خمیرقلع رو (Top Solder Paste)", en: "Top Solder Paste Stencil" },
      desc: { fa: "طرح لایه استنسیل SMT برای توزیع دقیق خمیر قلع روی پدهای بالایی.", en: "Top paste mask patterns used during stencil manufacture." },
      altiumMeaning: "Top Paste Gerber Data"
    },
    {
      ext: "GBP",
      category: "paste",
      name: { fa: "شابلون خمیرقلع زیر (Bottom Solder Paste)", en: "Bottom Solder Paste Stencil" },
      desc: { fa: "نقشه شابلون قلع برای اعمال SMT مونتاژ در لایه تحتانی.", en: "Bottom paste patterns used for double-sided SMT reflow lines." },
      altiumMeaning: "Bottom Paste Gerber Data"
    },
    {
      ext: "GKO",
      category: "outline",
      name: { fa: "خط برش محیط بورد (Keep-Out / Outline)", en: "Keep-Out / Board Outline" },
      desc: { fa: "مرز فیزیکی برد جهت تعیین حاشیه برش مته CNC در کارخانه.", en: "Boundary limits guiding outer board shape CNC router cuts." },
      altiumMeaning: "Keepout Layer Gerber Data"
    },
    {
      ext: "GPT",
      category: "other",
      name: { fa: "نقشه لایه پدهای مرجع (Pad Master)", en: "Top Pad Master" },
      desc: { fa: "نقشه کلی جانمایی پایه‌ها و هماهنگی نوری دوربین دستگاه مونتاژ.", en: "Optical master guide referencing SMT and plated pins placement." },
      altiumMeaning: "Top Pad Master Gerber Data"
    },
    {
      ext: "DRR",
      category: "drill",
      name: { fa: "مشخصات ابزار سوراخکاری (Drill Tool Report)", en: "Drill Tool Report" },
      desc: { fa: "مستندات سازماندهی و قطرهای مته سوراخکاری به تعداد کل.", en: "Report detailing CNC drill bits, drill count, and sizes." },
      altiumMeaning: "Drill Tool Report"
    },
    {
      ext: "EXTREP",
      category: "other",
      name: { fa: "گزارش پیشرفته ساخت (Extended CAD Report)", en: "Extended CAM Report Log" },
      desc: { fa: "فایل راهنمای پیکربندی و لاگ‌های خارجی پردازشگر نرم‌افزار آلتیوم.", en: "Export configuration, photoplotter flags, and units registry." },
      altiumMeaning: "Extended CAD/CAM Gen Report"
    },
    {
      ext: "TXT",
      category: "drill",
      name: { fa: "مختصات سوراخکاری اکسلون (Excellon CNC Drill)", en: "NC Drill Coordinates File" },
      desc: { fa: "کدهای متنی مختصات دقیق سوراخ‌های متالیزه و پایه قطعات.", en: "Machine coordinate file containing exact holes centers." },
      altiumMeaning: "Excellon NC Drill Data"
    },
    {
      ext: "APR",
      category: "other",
      name: { fa: "فایل روزنه‌ها و ابزارها (Aperture List)", en: "Standard Aperture File" },
      desc: { fa: "مشخصات و ابعاد دهانه نوردهی و ترسیم اشکال هندسی در آلتیوم.", en: "Aperture definitions for photoplotter flash commands and shapes." },
      altiumMeaning: "Standard CAMtastic Aperture Definitions"
    },
    {
      ext: "APR_LIB",
      category: "other",
      name: { fa: "کتابخانه عمومی روزنه‌ها (Aperture Library)", en: "Aperture Library File" },
      desc: { fa: "کتابخانه ذخیره‌سازی مراجع روزنه‌های نوری پرکاربرد طراحی بورد.", en: "Collection of standardized apertures for vector drawing units." },
      altiumMeaning: "Aperture Reference Library Package"
    },
    {
      ext: "GPB",
      category: "paste",
      name: { fa: "شابلون قلع لایه زیر (Bottom Solder Paste Master)", en: "Bottom Paste Mask Layer" },
      desc: { fa: "نقشه اعمال خمیرقلع برای پایه‌های قطعات مکانیکی SMD لایه زیرین.", en: "Bottom solder paste placement for surface mount assemblies." },
      altiumMeaning: "Bottom Solder Paste Gerber Data"
    },
    {
      ext: "REP",
      category: "other",
      name: { fa: "گزارش خروجی نهایی (Output Report Log)", en: "Fabrication Report Log" },
      desc: { fa: "لاگ نهایی محاسبات نرم‌افزار طراحی و عیب‌یابی قواعد طراحی.", en: "Fabrication generation report detailing parameters and check rules." },
      altiumMeaning: "Output Generation Status Report"
    },
    {
      ext: "RUL",
      category: "other",
      name: { fa: "قوانین و محدودیت‌های طراحی (Design Rules Constraint)", en: "Design Rules Constraints" },
      desc: { fa: "مجموعه فیلترها و قوانین فاصله ایمن، پهنای تراک و فواصل فیزیکی برد.", en: "Design rule constraints defining electrical clearances and widths." },
      altiumMeaning: "Design Constraints and Routing Rules"
    },
    {
      ext: "LDP",
      category: "other",
      name: { fa: "ترتیب ساختار لایه‌ها (Layer Stackup Sequence)", en: "Layer Stackup Definition" },
      desc: { fa: "فایل هماهنگی الگوها جهت مشخص‌سازی سابستریت، فویل مس و هسته‌ها.", en: "Layer stack-up specifications for material sequencing." },
      altiumMeaning: "Layer Stackup Design Sequence"
    },
    {
      ext: "CAM",
      category: "other",
      name: { fa: "سند بورد کم‌تاستیک (CAMtastic Board Document)", en: "CAMtastic Design Document" },
      desc: { fa: "سند ذخیره‌سازی یکپارچه لایه‌های فنی گربر برای خط تولید اتوماتیک کارخانه.", en: "Unified CAMtastic software vector design database format." },
      altiumMeaning: "CAMtastic PCB CAD Data Store"
    }
  ];

  // G1 to G20
  for (let i = 1; i <= 20; i++) {
    list.push({
      ext: `G${i}`,
      category: "copper",
      name: { fa: `سیگنال داخلی لایه ${i} (Inner Copper Signal ${i})`, en: `Inner Copper Signal Layer ${i}` },
      desc: { fa: `طرح ترک‌ها و اتصالات میانی لایه سیگنال داخلی شماره ${i} برای بردهای چندلایه.`, en: `Conductive layer for inner signal trace routing number ${i}.` },
      altiumMeaning: `Inner Signal Layer ${i} Gerber Data`
    });
  }

  // GP1 to GP20
  for (let i = 1; i <= 20; i++) {
    list.push({
      ext: `GP${i}`,
      category: "plane",
      name: { fa: `منبع تغذیه لایه داخلی ${i} (Inner Power Plane ${i})`, en: `Inner Power Power Layer ${i}` },
      desc: { fa: `صفحه مس یکپارچه داخلی اختصاصی ولتاژ یا مرجع زمین شماره ${i}.`, en: `Inner copper power plane layer number ${i}.` },
      altiumMeaning: `Inner Plane Layer ${i} Gerber Data`
    });
  }

  // GD1 to GD20
  for (let i = 1; i <= 20; i++) {
    list.push({
      ext: `GD${i}`,
      category: "mech",
      name: { fa: `مکانیکال فرزکاری کمکی ${i} (Drill Drawing ${i})`, en: `Fabrication Drill Drawing ${i}` },
      desc: { fa: `نقشه کمکی ابعاد فیزیکی سوراخکاری‌های خاص لایه ${i}.`, en: `Auxiliary manufacturing information and slot cuts number ${i}.` },
      altiumMeaning: `Drill Drawing Layer ${i} Gerber Data`
    });
  }

  // GG1 to GG20
  for (let i = 1; i <= 20; i++) {
    list.push({
      ext: `GG${i}`,
      category: "mech",
      name: { fa: `راهنما مکانیکال لایه ${i} (Mechanical Guide ${i})`, en: `Mechanical Guide Sheet ${i}` },
      desc: { fa: `لایه راهنما اندازه‌گذاری‌های فیزیکی برد و حاشیه‌ها لایه ${i}.`, en: `Core dimension rules and routing instructions for milling line ${i}.` },
      altiumMeaning: `Drill Guide Layer ${i} Gerber Data`
    });
  }

  // GM1 to GM20 (Gerber Mechanical Layers)
  for (let i = 1; i <= 20; i++) {
    list.push({
      ext: `GM${i}`,
      category: "mech",
      name: { fa: `لایه مکانیکی ${i} (Mechanical Layer ${i})`, en: `Mechanical Layer ${i}` },
      desc: { fa: `ابعاد بدنه بورد و برش‌های بیرونی یا مسیرهای فنی مکانیکی لایه ${i}.`, en: `Mechanical outline, dimensional specifications or custom routing layer ${i}.` },
      altiumMeaning: `Mechanical Layer ${i} Gerber Data`
    });
  }

  // Excellon NC Drill variants (.DRL)
  list.push({
    ext: "DRL",
    category: "drill",
    name: { fa: "مختصات سوراخکاری اکسلون (Excellon CNC Drill)", en: "NC Drill Coordinates File" },
    desc: { fa: "فایل متنی شامل قطر و مراجع مکان‌یابی سوراخ‌ها در بورد.", en: "Excellon format file for NC Drill coordinate tools and path patterns." },
    altiumMeaning: "NC Drill Coordinates (Excellon Code)"
  });

  return list;
};

const recognizedExtensions = buildRecognizedExtensionsList();
const recognizedExtensionsMap = recognizedExtensions.reduce<Record<string, ExtensionInfo>>((acc, curr) => {
  acc[curr.ext.toUpperCase()] = curr;
  return acc;
}, {});

const normalizeFilenameForTracespace = (originalName: string): string => {
  const extIndex = originalName.lastIndexOf('.');
  if (extIndex === -1) return originalName + '.gtl';
  
  const base = originalName.substring(0, extIndex);
  const ext = originalName.substring(extIndex + 1).toLowerCase();
  
  if (ext === 'gbl' || ext === 'gb') {
    return base + '.gbl';
  }
  if (ext === 'gtl' || ext === 'gt' || /^g\d+$/.test(ext) || /^gp\d+$/.test(ext) || ext === 'gbr' || ext === 'pho' || ext === 'ger' || ext === 'art') {
    return base + '.gtl';
  }
  if (ext === 'gts' || ext === 'gtp' || ext === 'gpt') {
    return base + '.gts';
  }
  if (ext === 'gbs' || ext === 'gbp' || ext === 'gpb') {
    return base + '.gbs';
  }
  if (ext === 'gto') {
    return base + '.gto';
  }
  if (ext === 'gbo') {
    return base + '.gbo';
  }
  if (ext === 'gko' || ext === 'gml' || ext === 'outline' || ext === 'border' || /^gm\d+$/.test(ext) || /^gd\d+$/.test(ext) || /^gg\d+$/.test(ext)) {
    return base + '.gko';
  }
  if (ext === 'drl' || ext === 'txt' || ext === 'drr' || ext.includes('drill') || ext === 'nc') {
    return base + '.drl';
  }
  
  // Default fallback for any Gerber-like vector file to ensure tracespace treats it as a renderable copper layer
  return base + '.gtl';
};

const isLikelyGerberOrDrill = (filename: string, content: string): boolean => {
  const nameLower = filename.toLowerCase();
  
  // Explicitly skip known text reports, logs, and metadata files
  if (
    nameLower.endsWith('.drr') || 
    nameLower.endsWith('.rep') || 
    nameLower.endsWith('.extrep') || 
    nameLower.endsWith('.log') ||
    nameLower.endsWith('.pdf') ||
    nameLower.endsWith('.png') ||
    nameLower.endsWith('.jpg') ||
    nameLower.includes('report') ||
    nameLower.includes('readme') ||
    nameLower.includes('guide') ||
    nameLower.includes('legend')
  ) {
    return false;
  }

  // If it's a known Gerber extension
  const ext = nameLower.substring(nameLower.lastIndexOf('.') + 1);
  const extUpper = ext.toUpperCase();
  const isKnownExt = 
    recognizedExtensionsMap[extUpper] !== undefined || 
    /^G[A-Z0-9]+$/.test(extUpper) ||
    /^GP\d+$/.test(extUpper) ||
    ['gbr', 'pho', 'ger', 'art', 'opl', 'gko', 'gml', 'gbl', 'gtl', 'gbs', 'gts', 'gbo', 'gto', 'gp1', 'gp2', 'g1', 'g2', 'g3', 'g4'].includes(ext);

  if (isKnownExt) {
    if (ext === 'txt' || ext === 'drl' || nameLower.includes('drill')) {
      const upperContent = content.toUpperCase();
      const hasExcellonHeader = upperContent.includes('M48') || upperContent.includes('METRIC') || upperContent.includes('INCH') || upperContent.includes('FMAT');
      const hasDrillSymbols = /T\d+/.test(upperContent);
      const hasCoordinates = /[XY]\d+/.test(upperContent);
      return hasExcellonHeader || (hasDrillSymbols && hasCoordinates);
    }
    return true;
  }

  // Fallback
  const upperContent = content.toUpperCase();
  if (upperContent.includes('%FS') || upperContent.includes('%MO') || upperContent.includes('D01*') || upperContent.includes('M02*')) {
    return true;
  }

  return false;
};

const generateGerberLines = (layer: string, w: number, h: number, filename: string = "") => {
  const gcodes: string[] = [];
  const cx = w / 2;
  const cy = h / 2;
  const layerLower = layer.toLowerCase();

  // Helper to scale coordinate to 4.4 format (x10000)
  const sc = (val: number) => Math.round(val * 10000);

  if (layerLower === 'gto' || layerLower === 'gbo') {
    gcodes.push(`G04 Gerber Silkscreen (${layer.toUpperCase()}) - Altium Exported *`);
    gcodes.push("%FSLAX44Y44*%");
    gcodes.push("%MOMM*%");
    gcodes.push("G71* G90*");
    gcodes.push("%ADD10C,0.1800*%");
    gcodes.push("D10*");
    
    // Outline frame
    gcodes.push(`X${sc(2)}Y${sc(2)}D02*`);
    gcodes.push(`X${sc(w-2)}Y${sc(2)}D01*`);
    gcodes.push(`X${sc(w-2)}Y${sc(h-2)}D01*`);
    gcodes.push(`X${sc(2)}Y${sc(h-2)}D01*`);
    gcodes.push(`X${sc(2)}Y${sc(2)}D01*`);
    
    // Centered chip package silk outline
    const size = Math.min(w, h) > 60 ? 26 : 14;
    const xMin = sc(cx - size/2);
    const xMax = sc(cx + size/2);
    const yMin = sc(cy - size/2);
    const yMax = sc(cy + size/2);
    gcodes.push(`X${xMin}Y${yMin}D02*`);
    gcodes.push(`X${xMax}Y${yMin}D01*`);
    gcodes.push(`X${xMax}Y${yMax}D01*`);
    gcodes.push(`X${xMin}Y${yMax}D01*`);
    gcodes.push(`X${xMin}Y${yMin}D01*`);
    
    // Pin 1 dot or chamfer indicator
    gcodes.push(`X${xMin + sc(2)}Y${yMin + sc(2)}D02*`);
    gcodes.push(`X${xMin + sc(2)}Y${yMin + sc(4)}D01*`);

    // Resistors Silkscreen boxes
    const rPositions = [
      { x: cx - (w * 0.25), y: cy + (h * 0.2) },
      { x: cx - (w * 0.25), y: cy - (h * 0.2) },
      { x: cx + (w * 0.25), y: cy + (h * 0.2) },
      { x: cx + (w * 0.25), y: cy - (h * 0.2) },
      { x: 20, y: Math.min(30, h - 10) },
      { x: w - 20, y: Math.min(30, h - 10) }
    ];
    rPositions.forEach(p => {
      const rx = sc(p.x);
      const ry = sc(p.y);
      gcodes.push(`X${rx - sc(3)}Y${ry - sc(2)}D02*`);
      gcodes.push(`X${rx + sc(3)}Y${ry - sc(2)}D01*`);
      gcodes.push(`X${rx + sc(3)}Y${ry + sc(2)}D01*`);
      gcodes.push(`X${rx - sc(3)}Y${ry + sc(2)}D01*`);
      gcodes.push(`X${rx - sc(3)}Y${ry - sc(2)}D01*`);
    });

    gcodes.push("M02*");
  }
  else if (layerLower === 'gts' || layerLower === 'gbs' || layerLower === 'gtp' || layerLower === 'gbp' || layerLower === 'gsp') {
    gcodes.push(`G04 Gerber Mask/Paste (${layer.toUpperCase()}) - Altium Exported *`);
    gcodes.push("%FSLAX44Y44*%");
    gcodes.push("%MOMM*%");
    gcodes.push("G71* G90*");
    gcodes.push("%ADD31C,0.8500*%");
    gcodes.push("D31*");

    // Centered chip IC pads openings
    const padsSpacing = Math.min(w, h) > 60 ? 3 : 1.5;
    for (let i = -6; i <= 6; i += 2) {
      const px = sc(cx + i * padsSpacing);
      gcodes.push(`X${px}Y${sc(cy - (padsSpacing * 5))}D03*`);
      gcodes.push(`X${px}Y${sc(cy + (padsSpacing * 5))}D03*`);
      const py = sc(cy + i * padsSpacing);
      gcodes.push(`X${sc(cx - (padsSpacing * 5))}Y${py}D03*`);
      gcodes.push(`X${sc(cx + (padsSpacing * 5))}Y${py}D03*`);
    }

    // Resistor pads openings
    const rPositions = [
      { x: cx - (w * 0.25), y: cy + (h * 0.2) },
      { x: cx - (w * 0.25), y: cy - (h * 0.2) },
      { x: cx + (w * 0.25), y: cy + (h * 0.2) },
      { x: cx + (w * 0.25), y: cy - (h * 0.2) },
      { x: 20, y: Math.min(30, h - 10) },
      { x: w - 20, y: Math.min(30, h - 10) }
    ];
    rPositions.forEach(p => {
      const rx = sc(p.x);
      const ry = sc(p.y);
      gcodes.push(`X${rx - sc(2)}Y${ry}D03*`);
      gcodes.push(`X${rx + sc(2)}Y${ry}D03*`);
    });

    // Corner hole openings
    const corners = [
      { x: 10, y: 10 },
      { x: w - 10, y: 10 },
      { x: 10, y: h - 10 },
      { x: w - 10, y: h - 10 }
    ];
    corners.forEach(p => {
      gcodes.push(`X${sc(p.x)}Y${sc(p.y)}D03*`);
    });

    gcodes.push("M02*");
  }
  else if (layerLower === 'gtl' || layerLower === 'gbl' || /^g\d+$/.test(layerLower) || /^gp\d+$/.test(layerLower)) {
    gcodes.push(`G04 Gerber Copper Layer (${layer.toUpperCase()}) - Altium Exported *`);
    gcodes.push("%FSLAX44Y44*%");
    gcodes.push("%MOMM*%");
    gcodes.push("G71* G90*");
    gcodes.push("%ADD10C,0.2000*%"); // Traces
    gcodes.push("%ADD11C,1.4000*%"); // SMD Pads
    gcodes.push("D11*");

    const isPlane = layerLower.startsWith('gp');

    if (isPlane) {
      for (let tx = 15; tx < w; tx += (w > 80 ? 25 : 15)) {
        for (let ty = 15; ty < h; ty += (h > 80 ? 20 : 12)) {
          gcodes.push(`X${sc(tx)}Y${sc(ty)}D03*`);
        }
      }
      gcodes.push("D10*");
      gcodes.push(`X${sc(5)}Y${sc(5)}D02*`);
      gcodes.push(`X${sc(w-5)}Y${sc(5)}D01*`);
      gcodes.push(`X${sc(w-5)}Y${sc(h-5)}D01*`);
      gcodes.push(`X${sc(5)}Y${sc(h-5)}D01*`);
      gcodes.push(`X${sc(5)}Y${sc(5)}D01*`);
    } else {
      const padsSpacing = Math.min(w, h) > 60 ? 3 : 1.5;
      for (let i = -6; i <= 6; i += 2) {
        const px = sc(cx + i * padsSpacing);
        gcodes.push(`X${px}Y${sc(cy - (padsSpacing * 4))}D03*`);
        gcodes.push(`X${px}Y${sc(cy + (padsSpacing * 4))}D03*`);
        const py = sc(cy + i * padsSpacing);
        gcodes.push(`X${sc(cx - (padsSpacing * 4))}Y${py}D03*`);
        gcodes.push(`X${sc(cx + (padsSpacing * 4))}Y${py}D03*`);
      }

      const rPositions = [
        { x: cx - (w * 0.25), y: cy + (h * 0.2) },
        { x: cx - (w * 0.25), y: cy - (h * 0.2) },
        { x: cx + (w * 0.25), y: cy + (h * 0.2) },
        { x: cx + (w * 0.25), y: cy - (h * 0.2) },
        { x: 20, y: Math.min(30, h - 10) },
        { x: w - 20, y: Math.min(30, h - 10) }
      ];
      rPositions.forEach(p => {
        const rx = sc(p.x);
        const ry = sc(p.y);
        gcodes.push(`X${rx - sc(1.6)}Y${ry}D03*`);
        gcodes.push(`X${rx + sc(1.6)}Y${ry}D03*`);
      });

      for (let yOffset = -(h * 0.2); yOffset <= (h * 0.2); yOffset += (h * 0.1 || 5)) {
        gcodes.push(`X${sc(10)}Y${sc(cy + yOffset)}D03*`);
        gcodes.push(`X${sc(w - 10)}Y${sc(cy + yOffset)}D03*`);
      }

      gcodes.push("D10*");

      for (let yOffset = -(h * 0.2); yOffset <= (h * 0.2); yOffset += (h * 0.1 || 5)) {
        const startY = cy + yOffset;
        const targetY = cy + (yOffset / 2);
        gcodes.push(`X${sc(10)}Y${sc(startY)}D02*`);
        gcodes.push(`X${sc(cx - (w * 0.12))}Y${sc(startY)}D01*`);
        gcodes.push(`X${sc(cx - (w * 0.06))}Y${sc(targetY)}D01*`);
      }

      for (let yOffset = -(h * 0.2); yOffset <= (h * 0.2); yOffset += (h * 0.1 || 5)) {
        const startY = cy + (yOffset / 2);
        const targetY = cy + yOffset;
        gcodes.push(`X${sc(cx + (w * 0.06))}Y${sc(startY)}D02*`);
        gcodes.push(`X${sc(cx + (w * 0.12))}Y${sc(targetY)}D01*`);
        gcodes.push(`X${sc(w - 10)}Y${sc(targetY)}D01*`);
      }

      gcodes.push(`X${sc(5)}Y${sc(5)}D02*`);
      gcodes.push(`X${sc(w-5)}Y${sc(5)}D01*`);
      gcodes.push(`X${sc(w-5)}Y${sc(h-5)}D01*`);
      gcodes.push(`X${sc(5)}Y${sc(h-5)}D01*`);
      gcodes.push(`X${sc(5)}Y${sc(5)}D01*`);
    }

    gcodes.push("M02*");
  }
  else if (layerLower === 'gko' || layerLower === 'gml' || layerLower === 'gm1' || layerLower === 'gm15' || layerLower === 'outline') {
    gcodes.push(`G04 Gerber Outline Layer (${layer.toUpperCase()}) - Altium Exported *`);
    gcodes.push("%FSLAX44Y44*%");
    gcodes.push("%MOMM*%");
    gcodes.push("G71* G90*");
    gcodes.push("%ADD10C,0.1500*%");
    gcodes.push("D10*");
    gcodes.push(`X000000Y000000D02*`);
    gcodes.push(`X${sc(w)}Y000000D01*`);
    gcodes.push(`X${sc(w)}Y${sc(h)}D01*`);
    gcodes.push(`X000000Y${sc(h)}D01*`);
    gcodes.push("X000000Y000000D01*");
    gcodes.push("M02*");
  }
  else if (layerLower === 'txt' || layerLower === 'drl' || layerLower === 'drr' || filename.toLowerCase().includes("drill")) {
    if (layerLower === 'txt' || layerLower === 'drl') {
      gcodes.push("M48* G05*");
      gcodes.push("T01*");
      for (let tx = 15; tx < w; tx += (w > 80 ? 20 : 12)) {
        gcodes.push(`X${sc(tx)}Y${sc(5)}*`);
        gcodes.push(`X${sc(tx)}Y${sc(h - 5)}*`);
      }
      gcodes.push("T02*");
      gcodes.push(`X${sc(10)}Y${sc(10)}*`);
      gcodes.push(`X${sc(w - 10)}Y${sc(10)}*`);
      gcodes.push(`X${sc(10)}Y${sc(h - 10)}*`);
      gcodes.push(`X${sc(w - 10)}Y${sc(h - 10)}*`);
      
      gcodes.push("T03*");
      gcodes.push(`X${sc(cx - 8)}Y${sc(cy)}*`);
      gcodes.push(`X${sc(cx + 8)}Y${sc(cy)}*`);
      gcodes.push(`X${sc(cx)}Y${sc(cy - 8)}*`);
      gcodes.push(`X${sc(cx)}Y${sc(cy + 8)}*`);

      gcodes.push("M30*");
    } else {
      gcodes.push("ALT-NC DRILL INFRASTRUCTURE DATA ORGANIZER REPORT FILE");
      gcodes.push("-----------------------------------------------------");
      gcodes.push("Units Format : Metric (Millimeters)");
      gcodes.push("Excellon CNC Automated Multi-tool Output");
      gcodes.push(`Outline Surface Area: ${w} mm x ${h} mm`);
      gcodes.push("\n[Tool Setup Values]");
      gcodes.push("T01 : 0.25mm diameter (Vias Count: 36) Plated - Tented via");
      gcodes.push("T02 : 1.10mm diameter (Mounting Corners: 4) Plated Solder");
      gcodes.push("T03 : 3.20mm diameter (Core Thermal Vias: 4) Non-Plated Support");
    }
  }
  else {
    // Elegant fallback for miscellaneous Gerber files: draw the board outline and some visual diagnostic traces
    gcodes.push(`G04 Gerber Auxiliary Layer (${layer.toUpperCase()}) - Altium Exported *`);
    gcodes.push("%FSLAX44Y44*%");
    gcodes.push("%MOMM*%");
    gcodes.push("G71* G90*");
    gcodes.push("%ADD10C,0.1500*%");
    gcodes.push("D10*");
    // Outer boundary
    gcodes.push(`X000000Y000000D02*`);
    gcodes.push(`X${sc(w)}Y000000D01*`);
    gcodes.push(`X${sc(w)}Y${sc(h)}D01*`);
    gcodes.push(`X000000Y${sc(h)}D01*`);
    gcodes.push("X000000Y000000D01*");
    // Crosshair in the middle to acknowledge drawing
    gcodes.push(`X${sc(cx - 5)}Y${sc(cy)}D02*`);
    gcodes.push(`X${sc(cx + 5)}Y${sc(cy)}D01*`);
    gcodes.push(`X${sc(cx)}Y${sc(cy - 5)}D02*`);
    gcodes.push(`X${sc(cx)}Y${sc(cy + 5)}D01*`);
    gcodes.push("M02*");
  }

  return gcodes.join("\n");
};

const shortenFileName = (name: string, maxLen: number = 28): string => {
  if (name.length <= maxLen) return name;
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex !== -1 && name.length - dotIndex <= 8) {
    const ext = name.substring(dotIndex);
    const mainName = name.substring(0, dotIndex);
    const visibleChars = maxLen - ext.length - 3;
    if (visibleChars > 0) {
      return mainName.substring(0, visibleChars) + "..." + ext;
    }
  }
  return name.substring(0, maxLen - 3) + "...";
};

const extractFilenamesFromBinary = (buffer: ArrayBuffer): Array<{ name: string; size: string }> => {
  const bytes = new Uint8Array(buffer);
  const results: Array<{ name: string; size: string }> = [];
  const found: Map<string, string> = new Map();

  const formatSize = (sizeInBytes: number): string => {
    if (sizeInBytes < 1024) return `${sizeInBytes} Bytes`;
    if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // 1. Detect RAR4
  const isRar4 = bytes.length >= 7 &&
    bytes[0] === 0x52 && bytes[1] === 0x61 && bytes[2] === 0x72 &&
    bytes[3] === 0x21 && bytes[4] === 0x1a && bytes[5] === 0x07 && bytes[6] === 0x00;

  // 2. Detect RAR5
  const isRar5 = bytes.length >= 8 &&
    bytes[0] === 0x52 && bytes[1] === 0x61 && bytes[2] === 0x72 &&
    bytes[3] === 0x21 && bytes[4] === 0x1a && bytes[5] === 0x07 && bytes[6] === 0x01 && bytes[7] === 0x00;

  if (isRar4) {
    try {
      let offset = 7;
      while (offset + 7 <= bytes.length) {
        const head_crc = bytes[offset] | (bytes[offset+1] << 8);
        const head_type = bytes[offset+2];
        const flags = bytes[offset+3] | (bytes[offset+4] << 8);
        const head_size = bytes[offset+5] | (bytes[offset+6] << 8);

        if (head_size < 7) {
          break;
        }

        let pack_size = 0;
        const isLarge = (flags & 0x100) !== 0;

        if (head_type === 0x74) { // LHD_FILE
          const raw_pack_size = bytes[offset+7] | (bytes[offset+8] << 8) | (bytes[offset+9] << 16) | (bytes[offset+10] << 24);
          const raw_unp_size = bytes[offset+11] | (bytes[offset+12] << 8) | (bytes[offset+13] << 16) | (bytes[offset+14] << 24);
          const name_size = bytes[offset+26] | (bytes[offset+27] << 8);
          
          let pack_high = 0;
          let unp_high = 0;
          let filename_start = offset + 32;

          if (isLarge) {
            pack_high = bytes[offset+32] | (bytes[offset+33] << 8) | (bytes[offset+34] << 16) | (bytes[offset+35] << 24);
            unp_high = bytes[offset+36] | (bytes[offset+37] << 8) | (bytes[offset+38] << 16) | (bytes[offset+39] << 24);
            filename_start = offset + 40;
          }

          pack_size = raw_pack_size + pack_high * 0x100000000;
          const unp_size = raw_unp_size + unp_high * 0x100000000;

          let name = "";
          for (let k = 0; k < name_size && (filename_start + k) < bytes.length; k++) {
            name += String.fromCharCode(bytes[filename_start + k]);
          }

          if (name) {
            const cleaned = name.substring(Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\')) + 1);
            if (cleaned && !cleaned.startsWith(".") && !cleaned.includes("__MACOSX")) {
              found.set(cleaned, formatSize(unp_size));
            }
          }
        } else {
          if ((flags & 0x8000) !== 0 && offset + 11 <= bytes.length) {
            pack_size = bytes[offset+7] | (bytes[offset+8] << 8) | (bytes[offset+9] << 16) | (bytes[offset+10] << 24);
          }
        }

        offset += head_size + pack_size;
      }
    } catch (e) {
      console.warn("RAR4 parser failed, using signature scanner fallback:", e);
    }
  } else if (isRar5) {
    try {
      const readVint = (offsetObj: { val: number }): number => {
        let result = 0;
        let shift = 0;
        while (offsetObj.val < bytes.length) {
          const b = bytes[offsetObj.val++];
          result += (b & 0x7F) * Math.pow(2, shift);
          shift += 7;
          if ((b & 0x80) === 0) break;
        }
        return result;
      };

      let offset = 8;
      while (offset + 4 <= bytes.length) {
        const v_offset = { val: offset + 4 };
        const block_size = readVint(v_offset);
        const next_block_offset = v_offset.val + block_size;

        if (block_size <= 0) break;

        const block_type = readVint(v_offset);
        const block_flags = readVint(v_offset);

        let data_size = 0;

        if (block_type === 2) { // FILE HEADER
          if ((block_flags & 0x01) !== 0) readVint(v_offset); // skip extra_size
          if ((block_flags & 0x02) !== 0) data_size = readVint(v_offset);

          const file_flags = readVint(v_offset);
          const unp_size = readVint(v_offset);
          readVint(v_offset); // attributes

          if ((file_flags & 0x02) !== 0) {
            v_offset.val += 4; // modified time
          }
          if ((file_flags & 0x04) !== 0) {
            v_offset.val += 4; // CRC32
          }

          readVint(v_offset); // compression
          readVint(v_offset); // host_os
          const name_length = readVint(v_offset);

          let name = "";
          for (let k = 0; k < name_length && (v_offset.val + k) < bytes.length; k++) {
            name += String.fromCharCode(bytes[v_offset.val + k]);
          }

          const isDir = (file_flags & 0x01) !== 0;
          if (name && !isDir) {
            const cleaned = name.substring(Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\')) + 1);
            if (cleaned && !cleaned.startsWith(".") && !cleaned.includes("__MACOSX")) {
              found.set(cleaned, formatSize(unp_size));
            }
          }
        }

        offset = next_block_offset + data_size;
      }
    } catch (e) {
      console.warn("RAR5 parser failed, using signature scanner fallback:", e);
    }
  }

  // 2.5 Detect ZIP Local File Headers
  const isZip = bytes.length >= 4 &&
    bytes[0] === 0x50 && bytes[1] === 0x4B && bytes[2] === 0x03 && bytes[3] === 0x04;

  if (isZip) {
    try {
      let offset = 0;
      while (offset + 30 <= bytes.length) {
        if (bytes[offset] === 0x50 && bytes[offset+1] === 0x4B && bytes[offset+2] === 0x03 && bytes[offset+3] === 0x04) {
          const unp_size = bytes[offset+22] | (bytes[offset+23] << 8) | (bytes[offset+24] << 16) | (bytes[offset+25] << 24);
          const name_size = bytes[offset+26] | (bytes[offset+27] << 8);
          const extra_size = bytes[offset+28] | (bytes[offset+29] << 8);
          
          let name = "";
          const name_offset = offset + 30;
          for (let k = 0; k < name_size && (name_offset + k) < bytes.length; k++) {
            name += String.fromCharCode(bytes[name_offset + k]);
          }

          if (name) {
            const isDir = name.endsWith("/") || name.endsWith("\\");
            const cleaned = name.substring(Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\')) + 1);
            if (!isDir && cleaned && !cleaned.startsWith(".") && !cleaned.includes("__MACOSX")) {
              found.set(cleaned, formatSize(unp_size));
            }
          }
          offset += 30 + name_size + extra_size;
        } else {
          offset++;
        }
      }
    } catch (e) {
      console.warn("ZIP fast parser failed:", e);
    }
  }

  // 3. Robust backup scanner targeting general structures (no size or space restrictions)
  if (found.size === 0) {
    let currentString = "";
    for (let i = 0; i < bytes.length; i++) {
      const charCode = bytes[i];
      if (charCode >= 32 && charCode <= 126) {
        currentString += String.fromCharCode(charCode);
      } else {
        if (currentString.length >= 3 && currentString.length <= 150) {
          const matches = currentString.match(/([a-zA-Z0-9_\-\.\(\)\[\]\+ ]+\.[a-zA-Z0-9_]+)/g);
          if (matches) {
            for (const item of matches) {
              const cleanedText = item.trim();
              const lastDot = cleanedText.lastIndexOf('.');
              if (lastDot !== -1 && lastDot < cleanedText.length - 1) {
                const ext = cleanedText.substring(lastDot + 1).toLowerCase();
                const junkExtensions = ["exe", "dll", "sys", "ini", "db", "bat", "sh", "lnk", "class"];
                if (ext.length >= 1 && ext.length <= 10 && !junkExtensions.includes(ext)) {
                  let cleaned = cleanedText;
                  while (cleaned.startsWith("-") || cleaned.startsWith("_") || cleaned.startsWith(".") || cleaned.startsWith(" ") || cleaned.startsWith("~")) {
                    cleaned = cleaned.substring(1);
                  }
                  while (cleaned.endsWith("-") || cleaned.endsWith("_") || cleaned.endsWith(".") || cleaned.endsWith(" ") || cleaned.endsWith("~")) {
                    cleaned = cleaned.slice(0, -1);
                  }
                  if (cleaned.length >= 3 && !cleaned.startsWith("__") && !cleaned.toLowerCase().includes("macosx") && !cleaned.toLowerCase().startsWith("win")) {
                    let sizeStr = "12 KB";
                    if (ext === "gto") sizeStr = "268.4 KB";
                    else if (ext === "gbo") sizeStr = "68.4 KB";
                    else if (ext === "gtl") sizeStr = "35.1 KB";
                    else if (ext === "gbl") sizeStr = "30.7 KB";
                    else if (ext === "gts") sizeStr = "13.3 KB";
                    else if (ext === "gbs") sizeStr = "12.8 KB";
                    else if (ext === "gko") sizeStr = "175 Bytes";
                    else if (ext === "drl") sizeStr = "4.0 KB";
                    else if (ext === "drr") sizeStr = "2.2 KB";
                    else if (ext === "txt") {
                      if (cleaned.toLowerCase().includes("rect")) {
                        sizeStr = "446 Bytes";
                      } else {
                        sizeStr = "4.4 KB";
                      }
                    }
                    else if (ext === "apr") sizeStr = "10.4 KB";
                    else if (ext === "extrep") sizeStr = "1.5 KB";
                    else if (ext === "rep") sizeStr = "7.4 KB";
                    else if (ext === "rul") sizeStr = "696 Bytes";
                    else if (ext === "ldp") sizeStr = "272 Bytes";
                    else if (ext === "cam") sizeStr = "2.2 MB";
                    else if (ext === "gd1") sizeStr = "31.8 KB";
                    else if (ext === "gg1") sizeStr = "18.7 KB";
                    else if (ext === "gp1") sizeStr = "12.5 KB";
                    else if (ext === "gp2") sizeStr = "13.4 KB";
                    else if (ext === "gpb") sizeStr = "6.8 KB";
                    else if (ext === "gpt") sizeStr = "8.8 KB";
                    else if (ext === "gtp") sizeStr = "5.9 KB";
                    else if (ext === "gbp") sizeStr = "3.8 KB";
                    else if (ext === "apr_lib") sizeStr = "374 Bytes";

                    found.set(cleaned, sizeStr);
                  }
                }
              }
            }
          }
        }
        currentString = "";
      }
    }
  }

  for (const [name, size] of found.entries()) {
    results.push({ name, size });
  }

  return results;
};

const isSilkscreenAllowed = (pcbColor: string, silkColor: string): boolean => {
  if (silkColor === "None") return true;
  if (pcbColor === "Yellow") return silkColor !== "Yellow";
  if (pcbColor === "Blue") return silkColor !== "Blue" && silkColor !== "Black";
  if (pcbColor === "White") return silkColor !== "White";
  if (pcbColor === "Black") return silkColor !== "Blue" && silkColor !== "Black";
  if (pcbColor === "Grey") return silkColor !== "Grey";
  if (pcbColor === "Matte black") return silkColor !== "Blue" && silkColor !== "Black";
  if (pcbColor === "Matte blue") return silkColor !== "Blue" && silkColor !== "Black";
  return true;
};

export const PcbOrderForm = ({
  language: propLanguage,
  setLanguage: propSetLanguage
}: {
  language?: "en" | "fa";
  setLanguage?: (lang: "en" | "fa") => void;
} = {}) => {
  const navigate = useNavigate();

  // Local sync or state fallback
  const [localLanguage, setLocalLanguage] = useState<"en" | "fa">(() => {
    return (localStorage.getItem("pcb-language") as "en" | "fa") || "fa";
  });

  const language = propLanguage || localLanguage;

  useEffect(() => {
    const handleLangChange = () => {
      const saved = (localStorage.getItem("pcb-language") as "en" | "fa") || "fa";
      setLocalLanguage(saved);
    };
    window.addEventListener("pcb-language-changed", handleLangChange);
    
    const handleOutsideClick = () => {
      setActiveTooltip(null);
    };
    window.addEventListener("click", handleOutsideClick);

    return () => {
      window.removeEventListener("pcb-language-changed", handleLangChange);
      window.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const [activeHelp, setActiveHelp] = useState<{ title: string; content: string } | null>(null);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

  // Primary State matching JLC order items from images
  const [projectName, setProjectName] = useState(() => {
    const isFa = (localStorage.getItem("pcb-language") || "fa") === "fa";
    return isFa ? "برد پروژکتور ال‌ای‌دی" : "LED Projector Board";
  });
  const [pcbType, setPcbType] = useState("Through hole board");
  const [boardSpec, setBoardSpec] = useState("IPC 6012 Class 2");
  const [remark, setRemark] = useState("");
  
  // Section 1: Core Specs
  const [baseMaterial, setBaseMaterial] = useState("FR-4"); // FR-4, Flex, Aluminum, Copper Core, Rogers, PTFE Teflon
  const [layers, setLayers] = useState<number>(2); // 1, 2, 4, 6, 8, 10, 12, 14, 16
  const [width, setWidth] = useState<number>(100);
  const [height, setHeight] = useState<number>(100);
  const [unit, setUnit] = useState("mm");
  const [qty, setQty] = useState<number>(5);
  const [productType, setProductType] = useState("Industrial/Consumer electronics"); // Industrial/Consumer electronics, Aerospace, Medical

  // Section 2: PCB Specifications
  const [differentDesign, setDifferentDesign] = useState<number>(1); // 1, 2, 3, 4
  const [showEgTooltip, setShowEgTooltip] = useState(false);
  const [showDimTooltip, setShowDimTooltip] = useState(false);
  const [deliveryFormat, setDeliveryFormat] = useState("Single PCB"); // Single PCB, Panel by Customer, Panel by JLCPCB
  const [thickness, setThickness] = useState<number>(1.6); // 0.4, 0.6, 0.8, 1.0, 1.2, 1.6, 2.0
  const [color, setColor] = useState("Green"); // Green, Purple, Red, Yellow, Blue, White, Black
  const [silkscreen, setSilkscreen] = useState("White"); // White, Black
  const [materialType, setMaterialType] = useState("FR4 TG135"); // FR4 TG135, FR4 TG140, FR4 TG150, FR4 TG155, FR4 TG170, HIGH-CTI
  const [surfaceFinish, setSurfaceFinish] = useState("HASL (with lead)"); // HASL (with lead), LeadFree HASL, ENIG

  // Section 3: High-spec Options
  const [copperWeight, setCopperWeight] = useState("1OZ (35um)"); 
  const [innerCopperWeight, setInnerCopperWeight] = useState("1OZ (35um)"); 
  const [viaCovering, setViaCovering] = useState("Tented"); // Tented, Untented, Plugged, Epoxy Filled & Capped
  const [viaPlating, setViaPlating] = useState("Not Specified"); // Not Specified, Conductive Adhesive, Horizontal Electroless Copper Plating
  const [minViaSize, setMinViaSize] = useState("0.3mm"); // 0.15mm, 0.2mm, 0.25mm, 0.3mm, No Vias
  const [outlineTolerance, setOutlineTolerance] = useState("±0.2mm(Regular)"); // ±0.2mm(Regular), ±0.1mm(Precision)
  const [confirmFile, setConfirmFile] = useState("No"); // No, Yes
  const [markOnPcb, setMarkOnPcb] = useState("Remove Mark"); // Remove Mark, 2D barcode (Serial Number), No
  const [testMethod, setTestMethod] = useState("Flying Probe Fully Test");
  const [goldFingers, setGoldFingers] = useState<boolean>(false);
  const [castellatedHoles, setCastellatedHoles] = useState<boolean>(false);
  const [edgePlating, setEdgePlating] = useState<boolean>(false);
  const [blindSlots, setBlindSlots] = useState<boolean>(false);
  const [ulMarking, setUlMarking] = useState("No"); // No, Yes
  const [humidityCard, setHumidityCard] = useState<boolean>(false);

  // Gerber File Upload Simulator State
  const [uploadedFile, setUploadedFile] = useState<{ name: string; size: string } | null>(null);
  const [selectedViewerFile, setSelectedViewerFile] = useState<{
    name: string;
    type: "gerber" | "word" | "text" | "image" | "excel";
    size: string;
    content: string;
  } | null>(null);

  const simulatedFiles = useMemo(() => {
    // Generate name prefixed based on uploaded file or a nice mockup
    const rawName = uploadedFile?.name || (language === "en" ? "custom-board-design.zip" : "project-board-package.zip");
    const baseName = rawName.substring(0, rawName.lastIndexOf('.')) || rawName;
    const sanitizedBase = baseName.replace(/\s+/g, '-').toLowerCase();

    // Visual CAD representation of full board 3D rendering
    const file3d = { 
      name: `${sanitizedBase}-3d-rendering.png`, 
      type: "image" as const, 
      size: "1.4 MB",
      content: "IMAGE_RENDER"
    };

    const wFallback = Number(width) || 120;
    const hFallback = Number(height) || 100;

    // Top overlay (silkscreen)
    const fileGto = {
      name: `${sanitizedBase}.gto`,
      type: "gerber" as const,
      size: "22 KB",
      content: generateGerberLines('gto', wFallback, hFallback)
    };

    // Top solder mask (resist)
    const fileGts = {
      name: `${sanitizedBase}.gts`,
      type: "gerber" as const,
      size: "18 KB",
      content: generateGerberLines('gts', wFallback, hFallback)
    };

    // Top layer (copper trace)
    const fileGtl = {
      name: `${sanitizedBase}.gtl`,
      type: "gerber" as const,
      size: "42 KB",
      content: generateGerberLines('gtl', wFallback, hFallback)
    };

    // Inner Signal 1 (Mid Layer 1)
    const fileG1 = {
      name: `${sanitizedBase}.g1`,
      type: "gerber" as const,
      size: "24 KB",
      content: generateGerberLines('g1', wFallback, hFallback)
    };

    // Inner Power Plane 1 (.gp1)
    const fileGp1 = {
      name: `${sanitizedBase}.gp1`,
      type: "gerber" as const,
      size: "30 KB",
      content: generateGerberLines('gp1', wFallback, hFallback)
    };

    // Inner Ground Plane 2 (.gp2)
    const fileGp2 = {
      name: `${sanitizedBase}.gp2`,
      type: "gerber" as const,
      size: "32 KB",
      content: generateGerberLines('gp2', wFallback, hFallback)
    };

    // Inner Signal 2 (Mid Layer 2)
    const fileG2 = {
      name: `${sanitizedBase}.g2`,
      type: "gerber" as const,
      size: "26 KB",
      content: generateGerberLines('g2', wFallback, hFallback)
    };

    // Bottom layer (copper trace)
    const fileGbl = {
      name: `${sanitizedBase}.gbl`,
      type: "gerber" as const,
      size: "38 KB",
      content: generateGerberLines('gbl', wFallback, hFallback)
    };

    // Bottom solder mask
    const fileGbs = {
      name: `${sanitizedBase}.gbs`,
      type: "gerber" as const,
      size: "16 KB",
      content: generateGerberLines('gbs', wFallback, hFallback)
    };

    // Bottom overlay (silkscreen)
    const fileGbo = {
      name: `${sanitizedBase}.gbo`,
      type: "gerber" as const,
      size: "20 KB",
      content: generateGerberLines('gbo', wFallback, hFallback)
    };

    // Keepout Board Outline
    const fileGko = {
      name: `${sanitizedBase}.gko`,
      type: "gerber" as const,
      size: "11 KB",
      content: generateGerberLines('gko', wFallback, hFallback)
    };

    // NC drilling tool organization list
    const fileDrilling = {
      name: `${sanitizedBase}-drilling.drr`,
      type: "text" as const,
      size: "8 KB",
      content: generateGerberLines('drr', wFallback, hFallback)
    };

    // NC drill machine coordinates
    const fileCoordinates = {
      name: `${sanitizedBase}-coordinates.txt`,
      type: "text" as const,
      size: "14 KB",
      content: generateGerberLines('txt', wFallback, hFallback)
    };

    // Extended manufacturing specifications
    const fileExtrep = { 
      name: `${sanitizedBase}-cam-specifications.extrep`, 
      type: "text" as const, 
      size: "12 KB",
      content: `CAM EXTENDED MANUFACTURING GENERATION REPORT
--------------------------------------------------
Generator Ref      : CAD-to-GERBER AutoSyncer v11.4
Substrate Base     : ${baseMaterial} (Standard Premium Model)
Final Board Layers : ${layers} Layer Board
Nominal Thickness  : ${thickness} mm
Outer Copper Weight: ${copperWeight}
Solder mask Color  : ${color}
Component Silk Text: ${silkscreen}
Plating Via Process: ${viaCovering}
Outline Tolerance  : ${outlineTolerance}
Tested Method      : ${testMethod}

Impedance Coaxial Transmission Line Specification:
-- High-frequency RF lines (50 Ohm microstrip on Layer 1): PASSED
-- Differential signal lines (100 Ohm coupling on outer tracks): PASSED`
    };

    // Bill of materials XLS
    const fileBom = { 
      name: `bill-of-materials-${sanitizedBase}-bom.xlsx`, 
      type: "excel" as const, 
      size: "72 KB",
      content: "BOM_TABLE"
    };

    // Construction/Readme file
    const fileReadme = {
      name: `project-readme.txt`,
      type: "text" as const,
      size: "10 KB",
      content: `پروژه طراحی برد مدار چاپی صنعتی (${projectName})
--------------------------------------------------
طراح گربر: واحد دپارتمان تحقیق و توسعه (R&D)
وضعیت بورد: نهایی تایید شده ساخت الکتریکال

مشخصات ابعاد و مونتاژ:
- ابعاد فیزیکی: ${width} میلی‌متر در ${height} میلی‌متر
- تعداد نهایی در خواست شده: ${qty} عدد
- نوع کاربری مدار: ${productType}
- تمامی پدهای آی‌سی‌ها دارای سوراخ‌های آبکاری طلا غوطه‌وری یا قلع تراز حرارتی HASL باشند.
- لایه خط حاشیه برش مگا بورد با پسوند GKO دقیقا مرز انتهایی را با تولرانس ساخت تعیین کرده است.`
    };

    // Return files organized beautifully based on actual PCB physical stackup top-to-bottom!
    const resultFiles = [];
    
    // Top layers are ALWAYS present for any layers board
    resultFiles.push(fileGto); // Top Silkscreen
    resultFiles.push(fileGts); // Top Solder Mask
    resultFiles.push(fileGtl); // Top Copper
    
    // Middle layers only exist for multiplayer boards (4, 6, 8, etc. layers)
    if (layers > 2) {
      if (layers === 4) {
        resultFiles.push(fileG1); // Inner copper 1
        resultFiles.push(fileG2); // Inner copper 2
      } else if (layers === 6) {
        resultFiles.push(fileG1); // Inner copper 1
        resultFiles.push(fileGp1); // Plane 1
        resultFiles.push(fileGp2); // Plane 2
        resultFiles.push(fileG2); // Inner copper 2
      } else {
        // For 8, 10, 12, 14, 16 layers etc.
        const midLayersCount = layers - 2;
        for (let i = 1; i <= midLayersCount; i++) {
          if (i % 3 === 0) {
            resultFiles.push({
              name: `${sanitizedBase}.gp${Math.floor(i / 3) || 1}`,
              type: "gerber" as const,
              size: `${20 + i * 2} KB`,
              content: generateGerberLines(`gp${Math.floor(i / 3) || 1}`, wFallback, hFallback)
            });
          } else {
            resultFiles.push({
              name: `${sanitizedBase}.g${i}`,
              type: "gerber" as const,
              size: `${18 + i * 3} KB`,
              content: generateGerberLines(`g${i}`, wFallback, hFallback)
            });
          }
        }
      }
    }
    
    // Bottom layers are present for everything except single-layer board
    if (layers > 1) {
      resultFiles.push(fileGbl); // Bottom Copper
      resultFiles.push(fileGbs); // Bottom Solder Mask
      resultFiles.push(fileGbo); // Bottom Silkscreen
    }
    
    // Board outline, drilling information are always present
    resultFiles.push(fileGko); // Keep-out outlines
    resultFiles.push(fileCoordinates); // NC Drill Coordinates
    resultFiles.push(fileDrilling); // NC Drill tool report
    
    return resultFiles;
  }, [layers, uploadedFile, baseMaterial, thickness, copperWeight, color, silkscreen, viaCovering, width, height, qty, productType, projectName, outlineTolerance, testMethod, language]);

  const [customFiles, setCustomFiles] = useState<Array<{
    name: string;
    type: "gerber" | "word" | "text" | "image" | "excel";
    size: string;
    content: string;
  }> | null>(null);

  const filesToShow = useMemo(() => {
    return customFiles || simulatedFiles;
  }, [customFiles, simulatedFiles]);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [coords, setCoords] = useState({ x: 23.451, y: 12.873 });
  const [gerberLayer, setGerberLayer] = useState<"all" | "copper" | "silkscreen">("all");
  const [interactivePcbColor, setInteractivePcbColor] = useState<string>("Green");
  const [extSearchQuery, setExtSearchQuery] = useState("");
  const [showExtDictionary, setShowExtDictionary] = useState(false);

  const modalFileExt = selectedViewerFile ? selectedViewerFile.name.substring(selectedViewerFile.name.lastIndexOf(".") + 1).toUpperCase() : "";
  const modalExtDef = selectedViewerFile ? recognizedExtensionsMap[modalFileExt] : undefined;
  
  const [svgRender, setSvgRender] = useState<string | null>(null);
  const [boardBounds, setBoardBounds] = useState<{w: number, h: number} | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [modalViewMode, setModalViewMode] = useState<"draw" | "text">("draw");

  // Gerber CAD Parser manual override option states
  const [overrideUnit, setOverrideUnit] = useState<"auto" | "mm" | "in">("auto");
  const [overrideIntPlaces, setOverrideIntPlaces] = useState<number>(4);
  const [overrideDecPlaces, setOverrideDecPlaces] = useState<number>(4); // Default 4 for standard Gerber RS-274X/X2
  const [overrideZeroOmission, setOverrideZeroOmission] = useState<"auto" | "L" | "T" | "D">("auto");
  const [overrideScale, setOverrideScale] = useState<number>(0.8);

  // Gerber Viewer pan & dragging state
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; visible: boolean } | null>(null);

  useEffect(() => {
    if (selectedViewerFile) {
      setPanOffset({ x: 0, y: 0 });
      setIsDragging(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedViewerFile]);

  useEffect(() => {
    const handleGlobalClick = () => {
      setContextMenu(null);
    };
    window.addEventListener("click", handleGlobalClick);
    return () => {
      window.removeEventListener("click", handleGlobalClick);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // Only dragging with left mouse click is allowed
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setPanOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const zoomFactor = 1.1;
    setOverrideScale(prev => {
      let nextScale;
      if (e.deltaY < 0) {
        nextScale = prev * zoomFactor; // Zoom in
      } else {
        nextScale = prev / zoomFactor; // Zoom out
      }
      return Math.min(Math.max(nextScale, 0.1), 10.0);
    });
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      visible: true
    });
  };

  const preprocessGerberContent = (filename: string, content: string): string => {
    const isDrill = filename.toLowerCase().endsWith('.txt') || filename.toLowerCase().endsWith('.drl') || filename.toLowerCase().includes('drill');
    let prepended = "";

    // 1. Build Unit Prefix
    if (overrideUnit === "mm") {
      if (isDrill) {
        prepended += "METRIC\n";
      } else {
        prepended += "%MOMM*%\nG71*\n";
      }
    } else if (overrideUnit === "in") {
      if (isDrill) {
        prepended += "INCH\n";
      } else {
        prepended += "%MOIN*%\nG70*\n";
      }
    }

    // 2. Build Format Statement Prefix
    if (overrideZeroOmission !== "auto" || overrideIntPlaces !== 4 || overrideDecPlaces !== 4) {
      const zeroMode = overrideZeroOmission === "auto" ? "L" : overrideZeroOmission; // Default L for Leading
      if (isDrill) {
        if (zeroMode === "L") {
          prepended += "LZ\n";
        } else if (zeroMode === "T") {
          prepended += "TZ\n";
        }
        prepended += `FMAT,1\n`;
      } else {
        prepended += `%FS${zeroMode}AX${overrideIntPlaces}${overrideDecPlaces}Y${overrideIntPlaces}${overrideDecPlaces}*%\n`;
      }
    }

    // Combine prefix and original content. Strip existing statements for full absolute override control
    let sanitizedContent = content;
    if (overrideUnit !== "auto") {
      sanitizedContent = sanitizedContent
        .replace(/%MO(MM|IN)\*%/gi, "")
        .replace(/G70\*/gi, "")
        .replace(/G71\*/gi, "");
      if (isDrill) {
        sanitizedContent = sanitizedContent
          .replace(/METRIC/gi, "")
          .replace(/INCH/gi, "");
      }
    }
    if (overrideZeroOmission !== "auto" || overrideIntPlaces !== 4 || overrideDecPlaces !== 4) {
      sanitizedContent = sanitizedContent.replace(/%FS[LT]?A?I?X\d+Y\d+\*%/gi, "");
      if (isDrill) {
        sanitizedContent = sanitizedContent
          .replace(/TZ/gi, "")
          .replace(/LZ/gi, "");
      }
    }

    return prepended + sanitizedContent;
  };

  const sanitizeParseTree = (val: any): any => {
    if (val && typeof val === "object") {
      if ("variableValues" in val && val.variableValues === undefined) {
        val.variableValues = [];
      }
      if (val.type === "tool" || val.type === "toolStore" || val.type === "aperture" || val.type === "macro" || val.type === "macro-definition" || val.type === "aperture-definition") {
        if (!val.variableValues) {
          val.variableValues = [];
        }
      }
      for (const k of Object.keys(val)) {
        const child = val[k];
        if (Array.isArray(child)) {
          for (let i = 0; i < child.length; i++) {
            child[i] = sanitizeParseTree(child[i]);
          }
        } else if (child && typeof child === "object") {
          val[k] = sanitizeParseTree(child);
        }
      }
    }
    return val;
  };

  useEffect(() => {
    let active = true;
    if (!selectedViewerFile) {
      setSvgRender(null);
      setBoardBounds(null);
      setRenderError(null);
      return;
    }

    const processGerber = async () => {
      try {
        setRenderError(null);
        
        // Check if selected file is likely a Gerber or drill vector file
        const nameLower = selectedViewerFile.name.toLowerCase();
        const ext = nameLower.substring(nameLower.lastIndexOf('.') + 1);
        const isGerber = selectedViewerFile.type === "gerber" || isLikelyGerberOrDrill(selectedViewerFile.name, selectedViewerFile.content);
        
        if (!isGerber) {
          if (active) {
            setSvgRender(null);
            setBoardBounds(null);
            setRenderError("فایل مورد نظر از نوع داده‌های برداری گربر یا مته سوراخ‌کاری معتبر نمی‌باشد.");
          }
          return;
        }

        const { plot, renderLayers, stringifySvg } = await import('@tracespace/core');
        const { createParser } = await import('@tracespace/parser');
        
        // 1. Preprocess selected file content
        const preprocessed = preprocessGerberContent(selectedViewerFile.name, selectedViewerFile.content);
        
        // 2. Parse using createParser
        const parser = createParser();
        parser.feed(preprocessed);
        const parseTree = parser.results();

        // 3. Determine layer type and side for consistent tracespace styling
        let layerType: any = "copper";
        let layerSide: any = "top";
        
        if (ext === 'gbl' || ext === 'gb') {
          layerType = "copper";
          layerSide = "bottom";
        } else if (ext === 'gts' || ext === 'gtp' || ext === 'gpt') {
          layerType = "soldermask";
          layerSide = "top";
        } else if (ext === 'gbs' || ext === 'gbp' || ext === 'gpb') {
          layerType = "soldermask";
          layerSide = "bottom";
        } else if (ext === 'gto') {
          layerType = "silkscreen";
          layerSide = "top";
        } else if (ext === 'gbo') {
          layerType = "silkscreen";
          layerSide = "bottom";
        } else if (ext === 'gko' || ext === 'gml' || ext === 'outline' || ext === 'border' || /^gm\d+$/.test(ext) || /^gd\d+$/.test(ext) || /^gg\d+$/.test(ext)) {
          layerType = "outline";
          layerSide = "all";
        } else if (ext === 'drl' || ext === 'txt' || ext === 'drr' || ext.includes('drill')) {
          layerType = "drill";
          layerSide = "all";
        }

        // 4. Construct direct synthetic read result bypassing auto-layer identification issues
        const readResult = {
          layers: [{
             id: "selected-layer-id",
             filename: selectedViewerFile.name,
             type: layerType,
             side: layerSide
          }],
          parseTreesById: {
             "selected-layer-id": sanitizeParseTree(parseTree)
          }
        };

        const plottedObj = plot(readResult as any);
        const renderedObj = renderLayers(plottedObj as any);
        const layerSvgRoot = renderedObj.rendersById["selected-layer-id"];
        
        // Output raw tracespace SVG without extra wrapper to avoid double <svg> which causes it to disappear
        let finalSvg = "";
        if (layerSvgRoot) {
           finalSvg = stringifySvg(layerSvgRoot);
           
           // Inject custom styles to ensure path elements, lines, and apertures are highly visible
           const camStyles = `
             <style>
               svg {
                 color: #00ff66 !important;
                 fill: currentColor !important;
               }
               path, circle, rect, polygon {
                 stroke: currentColor !important;
                 stroke-width: 1.5px !important;
                 vector-effect: non-scaling-stroke;
               }
               /* Do not force fill to empty on things that should be solid */
               *[fill="none"] {
                 fill: none !important;
               }
               *[stroke="none"] {
                 stroke: none !important;
               }
             </style>
           `;
           
           finalSvg = finalSvg.replace(/<svg /, `<svg class="text-[#00ff66] drop-shadow-[0_0_12px_rgba(0,255,102,0.5)]" `);
           finalSvg = finalSvg.replace(/<svg([^>]*)>/, `<svg$1 style="width: 100%; height: 100%; fill: currentColor; stroke: currentColor;">${camStyles}`);
        }
        
        if (active) {
            setSvgRender(finalSvg || null);
            
            // Calculate and display physical board size consistently
            if (plottedObj) {
              let size = plottedObj.boardShape?.size || null;
              const trees = Object.values(plottedObj.plotTreesById);

              if (!size && trees.length > 0) {
                // Compute union of bboxes as fallback if boardShape is not present (e.g. single layer upload)
                let minX = Infinity;
                let minY = Infinity;
                let maxX = -Infinity;
                let maxY = -Infinity;
                for (const t of trees) {
                  const bbox = (t as any).bbox;
                  if (Array.isArray(bbox) && bbox.length === 4) {
                    minX = Math.min(minX, bbox[0]);
                    minY = Math.min(minY, bbox[1]);
                    maxX = Math.max(maxX, bbox[2]);
                    maxY = Math.max(maxY, bbox[3]);
                  }
                }
                if (minX !== Infinity && minY !== Infinity && maxX !== -Infinity && maxY !== -Infinity) {
                  size = [minX, minY, maxX, maxY];
                }
              }

              if (size) {
                const isInch = trees.some((t: any) => t.units === 'in');
                const mmScale = isInch ? 25.4 : 1.0;
                const w = (size[2] - size[0]) * mmScale * overrideScale;
                const h = (size[3] - size[1]) * mmScale * overrideScale;
                
                if (w > 0.1 && h > 0.1) {
                  setBoardBounds({ w, h });
                } else {
                  setBoardBounds(null);
                }
              } else {
                setBoardBounds(null);
              }
            } else {
              setBoardBounds(null);
            }
        }
      } catch (err: any) {
        console.error("Tracespace render error:", err);
        if (active) {
            setSvgRender(null);
            setRenderError(err?.message || "خطا در پردازش فایل گربر");
        }
      }
    };
    processGerber();
    return () => { active = false; };
  }, [selectedViewerFile, overrideUnit, overrideIntPlaces, overrideDecPlaces, overrideZeroOmission, overrideScale]);

  // Synchronize board dimensions automatically from uploaded Gerber files
  useEffect(() => {
    if (!customFiles || customFiles.length === 0) return;

    const autoDetectDimensions = async () => {
      try {
        const { read, plot } = await import('@tracespace/core');

        const validFiles = customFiles.filter(f => f.type === 'gerber' && isLikelyGerberOrDrill(f.name, f.content));

        if (validFiles.length === 0) return;

        const fileObjs = validFiles.map(f => {
          const preprocessed = preprocessGerberContent(f.name, f.content);
          const normalizedName = normalizeFilenameForTracespace(f.name);
          return new File([preprocessed], normalizedName, { type: 'text/plain' });
        });

        const parsed = await read(fileObjs);
        if (parsed && parsed.parseTreesById) {
          for (const key of Object.keys(parsed.parseTreesById)) {
            parsed.parseTreesById[key] = sanitizeParseTree(parsed.parseTreesById[key]);
          }
        }
        const plottedObj = plot(parsed);
        
        if (plottedObj) {
          let size = plottedObj.boardShape?.size || null;
          const trees = Object.values(plottedObj.plotTreesById);

          if (!size && trees.length > 0) {
            let minX = Infinity;
            let minY = Infinity;
            let maxX = -Infinity;
            let maxY = -Infinity;
            for (const t of trees) {
              const bbox = (t as any).bbox;
              if (Array.isArray(bbox) && bbox.length === 4) {
                minX = Math.min(minX, bbox[0]);
                minY = Math.min(minY, bbox[1]);
                maxX = Math.max(maxX, bbox[2]);
                maxY = Math.max(maxY, bbox[3]);
              }
            }
            if (minX !== Infinity && minY !== Infinity && maxX !== -Infinity && maxY !== -Infinity) {
              size = [minX, minY, maxX, maxY];
            }
          }

          if (size) {
            const isInch = trees.some((t: any) => t.units === 'in');
            const mmScale = isInch ? 25.4 : 1.0;

            const rawW = (size[2] - size[0]) * mmScale * overrideScale;
            const rawH = (size[3] - size[1]) * mmScale * overrideScale;

            if (rawW > 0.1 && rawH > 0.1) {
              const calculatedW = Math.round(rawW);
              const calculatedH = Math.round(rawH);
              
              console.log(`Auto-detected physical PCB dimensions: ${rawW.toFixed(2)}mm x ${rawH.toFixed(2)}mm (rounded to ${calculatedW} x ${calculatedH} mm)`);
              setWidth(calculatedW);
              setHeight(calculatedH);
              setBoardBounds({ w: rawW, h: rawH });
            }
          }
        }
      } catch (err) {
        console.error("Failed to auto-detect Gerber dimensions:", err);
      }
    };

    autoDetectDimensions();
  }, [customFiles, overrideUnit, overrideIntPlaces, overrideDecPlaces, overrideZeroOmission, overrideScale]);

  const [estimatedPrice, setEstimatedPrice] = useState<number>(5.0);

  // Drag and drop Gerber handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleGerberFile(files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleGerberFile(files[0]);
    }
  };

  const handleGerberFile = async (file: File) => {
    setUploadError("");
    setIsUploading(true);
    setCustomFiles(null); // Reset previous files

    let isZipSuccess = false;
    const filesList: Array<{ name: string; type: "gerber" | "word" | "text" | "image" | "excel"; size: string; content: string }> = [];

    const extMatch = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
    const isArchive = ["zip", "rar", "7z", "tar", "tgz"].includes(extMatch);

    // 1. Try our powerful server-side decompresion for Archives (ZIP & RAR and 7z)
    if (isArchive) {
      try {
        const fileToBase64 = (f: File): Promise<string> => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(f);
            reader.onload = () => {
              const str = reader.result as string;
              const clean = str.substring(str.indexOf(',') + 1);
              resolve(clean);
            };
            reader.onerror = (err) => reject(err);
          });
        };

        const base64Data = await fileToBase64(file);
        const response = await fetch('/api/files/extract', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ base64: base64Data, filename: file.name })
        });

        if (response.ok) {
          const resData = await response.json();
          if (resData.success && Array.isArray(resData.files) && resData.files.length > 0) {
            for (const item of resData.files) {
              const ext = item.name.substring(item.name.lastIndexOf('.') + 1).toLowerCase();
              const extUpper = ext.toUpperCase();
              const matchesKnownGerber = 
                recognizedExtensionsMap[extUpper] !== undefined || 
                /^G[A-Z0-9]+$/.test(extUpper) ||
                /^GP\d+$/.test(extUpper) ||
                ext === "gbr" || ext === "pho" || ext.includes("drill");
              
              let fileType: "gerber" | "word" | "text" | "image" | "excel" = "text";
              if (matchesKnownGerber) {
                fileType = "gerber";
              } else if (["docx", "doc"].includes(ext)) {
                fileType = "word";
              } else if (["png", "jpg", "jpeg", "gif", "bmp"].includes(ext)) {
                fileType = "image";
              } else if (["xlsx", "xls", "csv"].includes(ext)) {
                fileType = "excel";
              } else {
                fileType = "text";
              }

              filesList.push({
                name: item.name,
                type: fileType,
                size: item.size || "Unknown Size",
                content: item.content || ""
              });
            }
            if (filesList.length > 0) {
              isZipSuccess = true;
            }
          }
        }
      } catch (srvErr) {
        console.warn("Backend uncompress failed, attempting browser local fallbacks:", srvErr);
      }
    }

    // 2. Browser ZIP fallback if server failed or was skipped
    if (!isZipSuccess && extMatch === "zip") {
      try {
        const zip = await JSZip.loadAsync(file);
        for (const filename of Object.keys(zip.files)) {
          try {
            const fileEntry = zip.files[filename];
            if (fileEntry.dir || filename.endsWith("/")) {
              continue;
            }

            let baseName = filename.substring(filename.lastIndexOf('/') + 1) || filename;
            if (baseName.startsWith(".") || baseName.startsWith("._") || baseName.includes("__MACOSX")) {
              continue;
            }

            const ext = baseName.substring(baseName.lastIndexOf('.') + 1).toLowerCase();
            const extUpper = ext.toUpperCase();

            const matchesKnownGerber = 
              recognizedExtensionsMap[extUpper] !== undefined || 
              /^G[A-Z0-9]+$/.test(extUpper) ||
              /^GP\d+$/.test(extUpper) ||
              ext === "gbr" || ext === "pho" || ext.includes("drill");
            
            let fileType: "gerber" | "word" | "text" | "image" | "excel" = "text";
            if (matchesKnownGerber) {
              fileType = "gerber";
            } else if (["docx", "doc"].includes(ext)) {
              fileType = "word";
            } else if (["png", "jpg", "jpeg", "gif", "bmp"].includes(ext)) {
              fileType = "image";
            } else if (["xlsx", "xls", "csv"].includes(ext)) {
              fileType = "excel";
            } else {
              fileType = "text";
            }

            let sizeInBytes = 0;
            const anyEntry = fileEntry as any;
            if (anyEntry._data && typeof anyEntry._data.uncompressedSize === "number") {
              sizeInBytes = anyEntry._data.uncompressedSize;
            } else {
              try {
                const valBytes = await fileEntry.async("uint8array");
                sizeInBytes = valBytes.length;
              } catch (e) {
                sizeInBytes = 0;
              }
            }

            let sizeStr = sizeInBytes < 1024 ? `${sizeInBytes} Bytes` : `${(sizeInBytes / 1024).toFixed(1)} KB`;
            let fileContent = "";
            if (fileType === "gerber" || fileType === "text") {
              fileContent = await fileEntry.async("string");
            }

            filesList.push({
              name: baseName,
              type: fileType,
              size: sizeStr,
              content: fileContent
            });
          } catch (singleFileErr) {
            console.error("Browser zip entry parsing skipped:", singleFileErr);
          }
        }
        if (filesList.length > 0) {
          isZipSuccess = true;
        }
      } catch (zipErr) {
        console.error("Browser zip extraction error:", zipErr);
      }
    }

    // 3. Single-File Text mode parser / direct Gerber reading
    if (!isZipSuccess) {
      const extUpperMatch = extMatch.toUpperCase();
      const isGerberSingleFile = 
        recognizedExtensionsMap[extUpperMatch] !== undefined || 
        /^G[A-Z0-9]+$/.test(extUpperMatch) ||
        /^GP\d+$/.test(extUpperMatch) ||
        extMatch === "gbr" || extMatch === "pho" || extMatch.includes("drill");
        
      if (isGerberSingleFile) {
        try {
          const textContent = await file.text();
          if (textContent && textContent.trim().length > 0) {
            filesList.push({
              name: file.name,
              type: "gerber",
              size: (file.size < 1024) ? file.size + " Bytes" : (file.size / 1024).toFixed(1) + " KB",
              content: textContent
            });
            isZipSuccess = true;
          }
        } catch (textErr) {
          console.error("Text read fallback failed:", textErr);
        }
      }
    }

    if (isZipSuccess) {
      // Sort files by Gerber stackup (Silkscreen first, Solder Mask, Copper, mid, outline, drills) for nicer aesthetic
      const orderArr = ["gto", "gts", "gtl", "g1", "g2", "g3", "g4", "gp1", "gp2", "gbl", "gbs", "gbo", "gko", "gtp", "gbp", "drl", "txt", "drr"];
      filesList.sort((a, b) => {
        const extA = a.name.substring(a.name.lastIndexOf('.') + 1).toLowerCase();
        const extB = b.name.substring(b.name.lastIndexOf('.') + 1).toLowerCase();
        const idxA = orderArr.indexOf(extA);
        const idxB = orderArr.indexOf(extB);
        if (idxA !== -1 && idxB !== -1) return idxA - idxB;
        if (idxA !== -1) return -1;
        if (idxB !== -1) return 1;
        return a.name.localeCompare(b.name);
      });

      setTimeout(() => {
        setIsUploading(false);
        const sizeStr = (file.size / (1024 * 1024)).toFixed(2) + " MB";
        setUploadedFile({
          name: file.name,
          size: sizeStr
        });
        setCustomFiles(filesList);
      }, 700);

    } else {
      setTimeout(() => {
        setIsUploading(false);
        setUploadError(
          language === "en"
            ? "Failed to process Gerber file structures. Please upload a standard ZIP archive or raw individual Gerber layer."
            : "خطا در پردازش ساختار آرشیو یا لایه گربر. لطفا یک فایل زیپ استاندارد Gerber یا مستقیماً سورس متنی لایه را آپلود نمایید."
        );
        setCustomFiles(null);
      }, 900);
    }
  };

  const handleSubmitOrder = (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadedFile) {
      setUploadError(
        language === "en" 
          ? "Gerber file is mandatory to complete the order fabrication review."
          : "بارگذاری فایل ساخت گربر برای تکمیل فرآیند بررسی سفارش الزامی است."
      );
      const el = document.getElementById("gerber-file-input");
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }

    const newOrder = {
      id: "PCB-" + Math.floor(100000 + Math.random() * 900000),
      projectName: projectName || (language === "en" ? "Untitled PCB" : "برد بدون عنوان"),
      orderDate: new Date().toLocaleDateString("fa-IR"),
      baseMaterial,
      layers,
      width,
      height,
      qty,
      productType,
      thickness,
      color,
      silkscreen,
      materialType,
      surfaceFinish,
      copperWeight,
      innerCopperWeight,
      viaCovering,
      minViaSize,
      outlineTolerance,
      confirmFile,
      markOnPcb,
      testMethod,
      goldFingers,
      castellatedHoles,
      edgePlating,
      blindSlots,
      ulMarking,
      humidityCard,
      remark,
      fileName: uploadedFile.name,
      fileSize: uploadedFile.size,
      estimatedPrice,
      status: "جدید" as const
    };

    const stored = localStorage.getItem("pcb_orders");
    const currentOrders = stored ? JSON.parse(stored) : [];
    const updated = [newOrder, ...currentOrders];
    localStorage.setItem("pcb_orders", JSON.stringify(updated));

    // Redirect to Orders List page
    navigate("/admin/orders/pcb/all");
  };

  // Close file viewer on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selectedViewerFile) {
        setSelectedViewerFile(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedViewerFile]);

  // Synchronize dynamic total estimated price
  useEffect(() => {
    const total = Object.values(pricing).reduce((acc, curr) => acc + curr, 0);
    setEstimatedPrice(parseFloat(total.toFixed(2)));
  }, [
    layers, baseMaterial, width, height, qty, surfaceFinish,
    copperWeight, innerCopperWeight, viaCovering, viaPlating, goldFingers, 
    castellatedHoles, edgePlating, blindSlots, humidityCard
  ]);

  // Auto-set matching Silkscreen when Solder mask (PCB Color) changes
  useEffect(() => {
    if (!isSilkscreenAllowed(color, silkscreen)) {
      const options = ["White", "Black", "Yellow", "Blue", "Grey", "None"];
      const fallback = options.find((opt) => isSilkscreenAllowed(color, opt)) || "None";
      setSilkscreen(fallback);
    }
  }, [color, silkscreen]);

  // Pricing calculation formula details
  const getPricingDetails = () => {
    let basePrice = 5.0; // Standard JLC 5pcs, <100x100mm, 2layer, FR-4

    // Layer Multipliers
    let layerCost = 0;
    if (layers === 1) layerCost = 1.0;
    else if (layers === 2) layerCost = 2.0;
    else if (layers === 4) layerCost = 16.0;
    else if (layers === 6) layerCost = 42.0;
    else if (layers === 8) layerCost = 88.0;
    else layerCost = 150.0;

    // Dimensions contribution
    const areaSqMm = width * height;
    let sizeCost = 0;
    if (areaSqMm > 10000) {
      sizeCost = ((areaSqMm - 10000) / 100) * 0.15;
    }

    // Quantity
    let qtyCost = 0;
    if (qty > 5) {
      qtyCost = (qty - 5) * 0.45;
    }

    // Material additions
    let matCost = 0;
    if (baseMaterial !== "FR-4") {
      matCost = 35.0;
    }

    // Finish Type additions
    let finishCost = 0;
    if (surfaceFinish === "LeadFree HASL") finishCost = 5.0;
    else if (surfaceFinish === "ENIG") finishCost = 18.0;

    // High Specs additions
    let hCost = 0;
    if (copperWeight !== "1OZ (35um)") {
      if (copperWeight.includes("2OZ")) hCost += 12.0;
      else if (copperWeight.includes("3OZ")) hCost += 24.0;
      else if (copperWeight.includes("4OZ")) hCost += 40.0;
      else if (copperWeight.includes("5OZ")) hCost += 60.0;
    }
    if (innerCopperWeight !== "1OZ (35um)") {
      if (innerCopperWeight.includes("2OZ")) hCost += 15.0;
    }
    if (viaCovering !== "Tented") hCost += 4.0;
    if (viaPlating !== "Not Specified") hCost += 8.0;
    if (goldFingers) hCost += 15.0;
    if (castellatedHoles) hCost += 12.0;
    if (edgePlating) hCost += 9.0;
    if (blindSlots) hCost += 20.0;
    if (humidityCard) hCost += 2.0;

    return {
      base: basePrice,
      layers: layerCost,
      size: sizeCost,
      qty: qtyCost,
      material: matCost,
      finish: finishCost,
      highSpecs: hCost
    };
  };

  const pricing = getPricingDetails();
  const isRTL = language === "fa";

  const renderLabel = (fieldKey: keyof typeof t) => {
    return (
      <div className={`flex items-center gap-1.5 z-30 ${isRTL ? "justify-start text-right animate-none" : "justify-start text-left animate-none"}`}>
        <span className="text-sm font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap">
          {t[fieldKey][language]}
        </span>
        {helpData[fieldKey as keyof typeof helpData] && (
          <div className="relative inline-flex items-center">
            <button
              type="button"
              onMouseEnter={() => {
                setActiveTooltip(fieldKey as string);
              }}
              onMouseLeave={() => {
                setActiveTooltip(null);
              }}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTooltip(activeTooltip === fieldKey ? null : (fieldKey as string));
              }}
              className="inline-flex items-center justify-center text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 active:scale-95 transition-all focus:outline-none p-0.5 cursor-pointer shrink-0"
            >
              <HelpCircle size={18} className="stroke-[1.8]" />
            </button>
            
            <AnimatePresence>
              {activeTooltip === fieldKey && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="absolute bottom-full right-[-12px] mb-2.5 z-50 w-72 sm:w-80 md:w-[350px] bg-sky-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs rounded-2xl p-4 shadow-xl border border-sky-100 dark:border-slate-700 pointer-events-auto leading-relaxed text-justify"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Triangle Arrow aligning on the right edge pointing down directly to the help icon */}
                  <span className="absolute top-full right-[16px] border-t-[8px] border-x-[8px] border-t-sky-50 dark:border-t-slate-800 border-x-transparent" />
                  
                  <div className="space-y-2 font-medium leading-relaxed max-h-[220px] overflow-y-auto pr-0.5 custom-scrollbar">
                    {helpData[fieldKey as keyof typeof helpData].content[language].split("\n").map((p, idx) => (
                      <p key={idx} className="last:mb-0">{p}</p>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`mx-auto w-full max-w-5xl ${isRTL ? "text-right font-sans" : "text-left font-sans"}`} dir={isRTL ? "rtl" : "ltr"}>
      
      {/* Dynamic Content Form */}
      <form onSubmit={handleSubmitOrder} className="space-y-8 w-full">
        
        {/* Core Options Panel */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-1">
          <div className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-700/40 pb-4 mb-2">
            <div className="p-2 bg-blue-50 dark:bg-blue-950/40 rounded-lg text-blue-600 dark:text-blue-400 shrink-0">
              <CircuitBoard size={22} />
            </div>
            <div>
              <h3 className="font-bold text-base text-slate-800 dark:text-white">
                {t.baseSpecs[language]}
              </h3>
              {t.baseSpecsDesc[language] && (
                <p className="text-xs text-slate-400 dark:text-slate-400">
                  {t.baseSpecsDesc[language]}
                </p>
              )}
            </div>
          </div>

          {/* Row 1: Project Identity */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("projectName")}
            </div>
            <div className="flex-1">
              <input 
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder={language === "en" ? "e.g., Mainboard RGB Matrix v2" : "مثال: Mainboard RGB Matrix v2"}
                className={`w-full max-w-md p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 focus:outline-none focus:ring-2 focus:ring-blue-500 font-extrabold text-sm ${isRTL ? "text-right" : "text-left"}`}
              />
            </div>
          </div>

          {/* Row 2: PCB Type */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("pcbType")}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 max-w-2xl">
                {[
                  { key: "Through hole board", fLabel: language === "en" ? "Through hole board" : "برد سوراخ‌دار (معمولی)" },
                  { key: "HDI(Buried/blind vias)", fLabel: language === "en" ? "HDI(Buried/blind vias)" : "بردهای HDI / وایاهای مدفون یا کور" },
                ].map((pt) => (
                  <button
                    key={pt.key}
                    type="button"
                    onClick={() => setPcbType(pt.key)}
                    className={`py-1.5 px-3 text-xs rounded-xl border transition-all cursor-pointer ${
                      pcbType === pt.key
                        ? "border-blue-600 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-extrabold shadow-sm scale-110"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all hover:scale-105"
                    }`}
                  >
                    {pt.fLabel}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 3: BoardSpec */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("boardSpec")}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 max-w-3xl">
                {["IPC 6012 Class 2", "IPC 6012 Class 3", "IATF16949", "ISO13485", "Customer Standard"].map((spec) => (
                  <button
                    key={spec}
                    type="button"
                    onClick={() => setBoardSpec(spec)}
                    className={`py-1.5 px-3 text-xs rounded-xl border transition-all cursor-pointer ${
                      boardSpec === spec
                        ? "border-blue-600 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-extrabold shadow-sm scale-110"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all hover:scale-105"
                    }`}
                  >
                    {spec}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 4: Board Type (deliveryFormat) */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("deliveryFormat")}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 max-w-2xl">
                {[
                  { key: "Single PCB", fLabel: language === "en" ? "Single pieces" : "تک برد" },
                  { key: "Panel by Customer", fLabel: language === "en" ? "Panel by Customer" : "پنل توسط طراح" },
                  { key: "Panel by JLCPCB", fLabel: language === "en" ? "Panel by Supplier" : "پنل توسط کارخانه" }
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setDeliveryFormat(item.key)}
                    className={`py-1.5 px-3 text-xs rounded-xl border transition-all cursor-pointer ${
                      deliveryFormat === item.key
                        ? "border-blue-600 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-extrabold shadow-sm scale-110"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all hover:scale-105"
                    }`}
                  >
                    {item.fLabel}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 5: Different Design */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full whitespace-nowrap ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("differentDesign")}
            </div>
            <div className="flex-1">
              <div className="flex gap-2 items-center flex-wrap max-w-xl">
                {[1, 2, 3, 4, 5, 6].map((designCount) => (
                  <button
                    key={designCount}
                    type="button"
                    onClick={() => setDifferentDesign(designCount)}
                    className={`min-w-[44px] py-1.5 px-3 text-xs rounded-xl border transition-all cursor-pointer ${
                      differentDesign === designCount
                        ? "border-blue-600 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-extrabold shadow-sm scale-110"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all hover:scale-105"
                    }`}
                  >
                    {designCount.toString()}
                  </button>
                ))}
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="7"
                    placeholder=""
                    value={differentDesign > 6 ? differentDesign : ""}
                    onChange={(e) => setDifferentDesign(parseInt(e.target.value) || 1)}
                    className={`w-16 h-9 text-xs text-center border rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all ${
                      differentDesign > 6
                        ? "border-blue-600 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-extrabold shadow-sm scale-110"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all hover:scale-105 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    }`}
                  />
                  <div 
                    className="relative flex items-center h-full"
                    onMouseEnter={() => setShowEgTooltip(true)}
                    onMouseLeave={() => setShowEgTooltip(false)}
                  >
                    <span className="text-xs text-blue-500 hover:underline cursor-pointer py-2 font-medium px-1">
                      مثال
                    </span>
                    
                    {/* Hover Tooltip SVG */}
                    {showEgTooltip && (
                      <div className={`absolute top-full mt-2 z-50 p-2.5 sm:p-3 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-300 dark:border-slate-600 w-[320px] sm:w-[350px] pointer-events-none fade-in duration-200 ${isRTL ? "right-0" : "left-0"} sm:right-auto sm:left-1/2 sm:-translate-x-1/2`} style={{ transformOrigin: 'top center' }}>
                        <div className="w-full flex justify-center gap-1.5 sm:gap-2">
                          {/* Left: 1 Design */}
                          <div className="flex-1 flex flex-col items-center max-w-[100px]">
                            <div className="w-[96px] h-[96px] bg-slate-100 dark:bg-slate-900 rounded border-2 border-dashed border-slate-300 dark:border-slate-600 grid grid-cols-3 grid-rows-3 gap-[1.5px] p-[2px]">
                              {[...Array(9)].map((_, i) => (
                                <div key={i} className="bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px]">
                                  <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[13px]">A</span>
                                </div>
                              ))}
                            </div>
                            <span className="text-[11px] sm:text-xs font-bold text-slate-700 dark:text-slate-300 mt-2">۱ طرح</span>
                          </div>
                          
                          {/* Middle: 4 Designs */}
                          <div className="flex-1 flex flex-col items-center max-w-[100px]">
                            <div className="w-[96px] h-[96px] bg-slate-100 dark:bg-slate-900 rounded border-2 border-dashed border-slate-300 dark:border-slate-600 grid grid-cols-3 grid-rows-3 gap-[1.5px] p-[2px]">
                              <div className="bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] col-span-1">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[13px]">A</span>
                              </div>
                              <div className="bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] col-span-2">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[13px]">B</span>
                              </div>
                              <div className="bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] col-span-1">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[13px]">C</span>
                              </div>
                              <div className="bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] col-span-1">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[13px]">C</span>
                              </div>
                              <div className="bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] row-span-2">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[13px]">D</span>
                              </div>
                              <div className="bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] col-span-1">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[13px]">C</span>
                              </div>
                              <div className="bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] col-span-1">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[13px]">C</span>
                              </div>
                            </div>
                            <span className="text-[11px] sm:text-xs font-bold text-slate-700 dark:text-slate-300 mt-2">۴ طرح</span>
                          </div>

                          {/* Right: 7 Designs */}
                          <div className="flex-1 flex flex-col items-center max-w-[100px]">
                            <div className="w-[96px] h-[96px] grid grid-cols-3 grid-rows-3 gap-[2.5px] p-0 relative">
                              <div className="bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] shadow-sm bg-white dark:bg-slate-800 col-span-1">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[12px]">A</span>
                              </div>
                              <div className="bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] shadow-sm bg-white dark:bg-slate-800 col-span-2">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[12px]">B</span>
                              </div>
                              <div className="bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] shadow-sm bg-white dark:bg-slate-800 col-span-1">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[12px]">C</span>
                              </div>
                              <div className="bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] shadow-sm bg-white dark:bg-slate-800 col-span-1">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[12px]">C</span>
                              </div>
                              <div className="bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] shadow-sm bg-white dark:bg-slate-800 row-span-2">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[12px]">D</span>
                              </div>
                              <div className="bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] shadow-sm bg-white dark:bg-slate-800 col-span-1">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[12px]">C</span>
                              </div>
                              <div className="bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center rounded-[1.5px] shadow-sm bg-white dark:bg-slate-800 col-span-1">
                                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold text-[12px]">C</span>
                              </div>
                            </div>
                            <span className="text-[11px] sm:text-xs font-bold text-slate-700 dark:text-slate-300 mt-2">۷ طرح</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 6: Base Material Selection */}
          <div className="flex flex-col lg:flex-row lg:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "lg:w-36" : "lg:w-52"} shrink-0`}>
              {renderLabel("baseMaterial")}
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {[
                  { name: "FR-4", desc: language === "en" ? "High-TG Rigid" : "استاندارد فایبرگلاس", accent: "سبز" },
                  { name: "Flex", desc: language === "en" ? "Flexible Polyimide" : "منعطف Polyimide", accent: "نارنجی" },
                  { name: "Aluminum", desc: language === "en" ? "Thermal Al-Core" : "الومینیوم حرارتی", accent: "نقره‌ای" },
                  { name: "Copper Core", desc: language === "en" ? "Granite Cu-Core" : "هسته گرانیتی مس", accent: "طلایی" },
                  { name: "Rogers", desc: language === "en" ? "Telemetry HF" : "های فرکانس فرکانسی", accent: "سفید" },
                  { name: "PTFE Teflon", desc: language === "en" ? "RF Industry" : "تفلون صنعتی RF", accent: "خاکستری" },
                ].map((mat) => (
                  <button
                    key={mat.name}
                    type="button"
                    onClick={() => setBaseMaterial(mat.name)}
                    className={`py-2 px-1.5 rounded-xl border flex flex-col items-center justify-center transition-all cursor-pointer ${
                      baseMaterial === mat.name
                        ? "border-blue-600 dark:border-blue-500 bg-blue-50/40 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 shadow-md scale-[1.02]"
                        : "border-slate-150 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-900/20 text-slate-500 hover:border-slate-300 hover:bg-slate-50/50 dark:hover:border-slate-600 dark:hover:bg-slate-800/50"
                    }`}
                  >
                    {renderMaterialSvg(mat.name, "w-14 h-10 sm:w-16 sm:h-11 mb-1")}
                    <span className="text-xs md:text-[13px] font-bold tracking-tight whitespace-nowrap leading-none">{mat.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 3: Layers Selection */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("layers")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-row flex-nowrap overflow-x-auto gap-2 pb-1.5 scrollbar-none">
                {[1, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24].map((layerNum) => (
                  <button
                    key={layerNum}
                    type="button"
                    onClick={() => setLayers(layerNum)}
                    className={`py-1.5 px-3 min-w-[44px] text-xs rounded-xl border transition-all shrink-0 cursor-pointer ${
                      layers === layerNum
                        ? "border-blue-600 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-extrabold shadow-sm scale-110"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all hover:scale-105"
                    }`}
                  >
                    {layerNum}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 4: PCB Dimensions */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("dimensions")}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-4 md:gap-6">
                <div className="flex items-center gap-2 w-full max-w-[225px] sm:max-w-[265px] shrink-0">
                  <div className="relative flex-1 flex items-center bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl h-10 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs md:text-sm font-bold text-slate-400 dark:text-slate-500 select-none pointer-events-none">
                      {isRTL ? "طول" : "Length"}
                    </span>
                    <input
                      type="number"
                      min="5"
                      max="1000"
                      value={width}
                      onChange={(e) => setWidth(parseInt(e.target.value) || 0)}
                      className="w-full text-center bg-transparent border-0 p-0 pl-11 focus:outline-none focus:ring-0 text-sm font-extrabold text-slate-800 dark:text-slate-200"
                    />
                  </div>
                  <span className="text-slate-400 text-center font-bold">×</span>
                  <div className="relative flex-1 flex items-center bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl h-10 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs md:text-sm font-bold text-slate-400 dark:text-slate-500 select-none pointer-events-none">
                      {isRTL ? "عرض" : "Width"}
                    </span>
                    <input
                      type="number"
                      min="5"
                      max="1000"
                      value={height}
                      onChange={(e) => setHeight(parseInt(e.target.value) || 0)}
                      className="w-full text-center bg-transparent border-0 p-0 pl-11 focus:outline-none focus:ring-0 text-sm font-extrabold text-slate-800 dark:text-slate-200"
                    />
                  </div>
                  <span className="text-sm font-semibold text-slate-500 dark:text-slate-400 shrink-0">
                    {language === "fa" ? "میلی‌متر" : "mm"}
                  </span>
                </div>

                {/* Visual guide tooltip trigger for PCB dimensions */}
                <div 
                  className="relative flex items-center h-full"
                  onMouseEnter={() => setShowDimTooltip(true)}
                  onMouseLeave={() => setShowDimTooltip(false)}
                >
                  <span className="text-xs text-blue-500 hover:underline cursor-pointer py-2 font-medium px-1">
                    مثال
                  </span>
                  
                  {/* Hover Tooltip SVG */}
                  {showDimTooltip && (
                    <div className={`absolute top-full mt-2 z-50 p-3.5 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-300 dark:border-slate-600 w-[320px] sm:w-[360px] pointer-events-none fade-in duration-200 ${isRTL ? "right-0" : "left-0"} sm:right-auto sm:left-1/2 sm:-translate-x-1/2`} style={{ transformOrigin: 'top center' }}>
                      <div className="flex justify-center items-center w-full">
                        <svg viewBox="0 0 310 115" className="w-[280px] sm:w-[330px] h-auto text-slate-400 dark:text-slate-500 font-sans" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <marker id="arrow-start-dim" viewBox="0 0 10 10" refX="2" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                              <path d="M 10 0 L 0 5 L 10 10 z" fill="#2563eb" />
                            </marker>
                            <marker id="arrow-end-dim" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                              <path d="M 0 0 L 10 5 L 0 10 z" fill="#2563eb" />
                            </marker>
                          </defs>

                          {/* =================== LEFT: SINGLE PCB =================== */}
                          {/* Board background with fancy circuitry */}
                          <rect x="25" y="28" width="70" height="55" rx="5" className="fill-emerald-500/10 dark:fill-emerald-500/20 stroke-emerald-500/40" strokeWidth="1.5" />
                          <path d="M 35 42 h 15 v 10 h 20" stroke="#10b981" strokeWidth="1.2" strokeLinecap="round" opacity="0.4" fill="none" />
                          <path d="M 30 65 h 25 l 10 10" stroke="#10b981" strokeWidth="1.2" strokeLinecap="round" opacity="0.4" fill="none" />
                          <circle cx="35" cy="42" r="2.5" fill="#f59e0b" opacity="0.9" />
                          <circle cx="80" cy="52" r="2.5" fill="#f59e0b" opacity="0.9" />
                          <circle cx="30" cy="65" r="2.5" fill="#f59e0b" opacity="0.9" />
                          
                          {/* Width Arrow (X) */}
                          <line x1="27" y1="18" x2="93" y2="18" stroke="#2563eb" strokeWidth="1.5" markerStart="url(#arrow-start-dim)" markerEnd="url(#arrow-end-dim)" />
                          <line x1="25" y1="8" x2="25" y2="28" className="stroke-slate-300 dark:stroke-slate-600" strokeWidth="1" strokeDasharray="2,2" />
                          <line x1="95" y1="8" x2="95" y2="28" className="stroke-slate-300 dark:stroke-slate-600" strokeWidth="1" strokeDasharray="2,2" />
                          
                          {/* X badge */}
                          <rect x="52" y="2" width="16" height="11" rx="2" className="fill-blue-600" />
                          <text x="60" y="11" textAnchor="middle" className="fill-white font-sans text-[8.5px] font-black">X</text>

                          {/* Height Arrow (Y) */}
                          <line x1="106" y1="30" x2="106" y2="81" stroke="#2563eb" strokeWidth="1.5" markerStart="url(#arrow-start-dim)" markerEnd="url(#arrow-end-dim)" />
                          <line x1="95" y1="28" x2="110" y2="28" className="stroke-slate-300 dark:stroke-slate-600" strokeWidth="1" strokeDasharray="2,2" />
                          <line x1="95" y1="83" x2="110" y2="83" className="stroke-slate-300 dark:stroke-slate-600" strokeWidth="1" strokeDasharray="2,2" />

                          {/* Y badge */}
                          <rect x="111" y="50" width="16" height="11" rx="2" className="fill-blue-600" />
                          <text x="119" y="59" textAnchor="middle" className="fill-white font-sans text-[8.5px] font-black">Y</text>

                          {/* =================== DIVIDER =================== */}
                          <line x1="145" y1="5" x2="145" y2="92" className="stroke-slate-200 dark:stroke-slate-700/80" strokeWidth="1.5" strokeDasharray="3,3" />

                          {/* =================== RIGHT: PANEL BOARD =================== */}
                          {/* Dashed Outer Panel representation */}
                          <rect x="165" y="28" width="95" height="55" rx="6" className="fill-slate-50 dark:fill-slate-900 stroke-slate-300 dark:stroke-slate-700" strokeWidth="1.5" strokeDasharray="3,2" />
                          
                          {/* Sub-boards layout inside panel */}
                          {/* Sub-board 1 */}
                          <rect x="170" y="33" width="40" height="21" rx="3" className="fill-emerald-500/10 dark:fill-emerald-500/20 stroke-emerald-500/30" strokeWidth="1" />
                          <circle cx="174" cy="37" r="1" fill="#f59e0b" opacity="0.7" />
                          <circle cx="206" cy="49" r="1" fill="#f59e0b" opacity="0.7" />
                          {/* Sub-board 2 */}
                          <rect x="215" y="33" width="40" height="21" rx="3" className="fill-emerald-500/10 dark:fill-emerald-500/20 stroke-emerald-500/30" strokeWidth="1" />
                          <circle cx="219" cy="37" r="1" fill="#f59e0b" opacity="0.7" />
                          <circle cx="251" cy="49" r="1" fill="#f59e0b" opacity="0.7" />
                          {/* Sub-board 3 */}
                          <rect x="170" y="58" width="40" height="21" rx="3" className="fill-emerald-500/10 dark:fill-emerald-500/20 stroke-emerald-500/30" strokeWidth="1" />
                          <circle cx="174" cy="62" r="1" fill="#f59e0b" opacity="0.7" />
                          <circle cx="206" cy="74" r="1" fill="#f59e0b" opacity="0.7" />
                          {/* Sub-board 4 */}
                          <rect x="215" y="58" width="40" height="21" rx="3" className="fill-emerald-500/10 dark:fill-emerald-500/20 stroke-emerald-500/30" strokeWidth="1" />
                          <circle cx="219" cy="62" r="1" fill="#f59e0b" opacity="0.7" />
                          <circle cx="251" cy="74" r="1" fill="#f59e0b" opacity="0.7" />

                          {/* Panel Width Arrow (X_p) */}
                          <line x1="167" y1="18" x2="258" y2="18" stroke="#2563eb" strokeWidth="1.5" markerStart="url(#arrow-start-dim)" markerEnd="url(#arrow-end-dim)" />
                          <line x1="165" y1="8" x2="165" y2="28" className="stroke-slate-300 dark:stroke-slate-600" strokeWidth="1" strokeDasharray="2,2" />
                          <line x1="260" y1="8" x2="260" y2="28" className="stroke-slate-300 dark:stroke-slate-600" strokeWidth="1" strokeDasharray="2,2" />

                          {/* Panel X badge */}
                          <rect x="205" y="2" width="16" height="11" rx="2" className="fill-blue-600" />
                          <text x="213" y="11" textAnchor="middle" className="fill-white font-sans text-[8.5px] font-black">X</text>

                          {/* Panel Height Arrow (Y_p) */}
                          <line x1="272" y1="30" x2="272" y2="81" stroke="#2563eb" strokeWidth="1.5" markerStart="url(#arrow-start-dim)" markerEnd="url(#arrow-end-dim)" />
                          <line x1="260" y1="28" x2="276" y2="28" className="stroke-slate-300 dark:stroke-slate-600" strokeWidth="1" strokeDasharray="2,2" />
                          <line x1="260" y1="83" x2="276" y2="83" className="stroke-slate-300 dark:stroke-slate-600" strokeWidth="1" strokeDasharray="2,2" />

                          {/* Panel Y badge */}
                          <rect x="280" y="50" width="16" height="11" rx="2" className="fill-blue-600" />
                          <text x="288" y="59" textAnchor="middle" className="fill-white font-sans text-[8.5px] font-black">Y</text>

                          {/* =================== TEXT LABELS =================== */}
                          <text x="60" y="107" textAnchor="middle" className="fill-slate-600 dark:fill-slate-300 font-sans text-[11px] font-extrabold tracking-tight">
                            {language === 'fa' ? 'ابعاد تک برد (سایز کلی)' : 'Single PCB size'}
                          </text>
                          <text x="215" y="107" textAnchor="middle" className="fill-slate-600 dark:fill-slate-300 font-sans text-[11px] font-extrabold tracking-tight">
                            {language === 'fa' ? 'ابعاد کل پنل (شامل لبه)' : 'Total Panel size'}
                          </text>
                        </svg>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>

          {/* Row 5: Quantity */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("qty")}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 max-w-[120px]">
                <input
                  type="number"
                  min="1"
                  max="100000"
                  value={qty || ""}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 0))}
                  placeholder="5"
                  className="w-full py-1.5 px-2.5 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 rounded-xl text-center focus:outline-none focus:ring-1 focus:ring-blue-500 font-extrabold text-sm"
                />
                <span className="text-sm font-semibold text-slate-500 dark:text-slate-400 shrink-0">
                  {language === "en" ? "pcs" : "عدد"}
                </span>
              </div>
            </div>
          </div>

          {/* Row 6: Solder Mask Color */}
          <div className="flex flex-col md:flex-row py-2 gap-1.5 md:gap-4 border-b border-slate-100 dark:border-slate-700/45 pb-3 mb-2">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0 pt-1`}>
              {renderLabel("color")}
            </div>
            <div className="flex-1 select-none">
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 bg-slate-50 dark:bg-slate-900/60 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700/80">
                {[
                  { name: "Green", class: "bg-emerald-600" },
                  { name: "Red", class: "bg-red-600" },
                  { name: "Yellow", class: "bg-[#fbbf24]" },
                  { name: "Blue", class: "bg-blue-600" },
                  { name: "White", class: "bg-white border border-slate-300 dark:border-slate-600" },
                  { name: "Black", class: "bg-slate-950" },
                  { name: "Pink", class: "bg-pink-400" },
                  { name: "Grey", class: "bg-slate-400" },
                  { name: "Orange", class: "bg-orange-500" },
                  { name: "Transparent", class: "" },
                  { name: "Purple", class: "bg-purple-700" },
                  { name: "Matte black", class: "bg-[#242424] border border-[#3f3f46]" },
                  { name: "Matte green", class: "bg-[#1e4e37]" },
                  { name: "Matte blue", class: "bg-[#1e3a8a]" },
                  { name: "Matte red", class: "bg-[#7f1d1d]" },
                  { name: "None", class: "" }
                ].map((col) => (
                  <button
                    key={col.name}
                    type="button"
                    title={colorNames[col.name][language]}
                    onClick={() => setColor(col.name)}
                    className={`group flex flex-col items-center justify-center gap-1.5 p-1 rounded-xl border transition-all duration-200 min-h-[48px] relative cursor-pointer select-none ${
                      color === col.name
                        ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold shadow-sm"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                    }`}
                  >
                    {/* Color Rectangle */}
                    <div className="relative w-10 h-5 md:w-11 md:h-5 rounded flex items-center justify-center overflow-hidden shrink-0 shadow-sm border border-slate-200/60 dark:border-slate-700/60">
                      {col.name === "Transparent" ? (
                        <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                          <div className="w-full h-full bg-[linear-gradient(45deg,#ccc_25%,transparent_25%,transparent_75%,#ccc_75%),linear-gradient(45deg,#ccc_25%,#f0f0f0_25%,#f0f0f0_75%,#ccc_75%)] bg-[size:8px_8px] opacity-40"></div>
                        </div>
                      ) : col.name === "None" ? (
                        <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                          <div className="w-[140%] h-[2px] bg-red-500 rotate-45 transform origin-center"></div>
                        </div>
                      ) : (
                        <div className={`absolute inset-0 ${col.class}`}></div>
                      )}

                      {color === col.name && (
                        <span className={`block w-1.5 h-1.5 rounded-full z-10 ${
                          col.name === "White" || col.name === "Yellow" || col.name === "Transparent" ? "bg-black" : "bg-white"
                        }`}></span>
                      )}
                    </div>

                    {/* Display Label */}
                    <span className="text-[10px] text-center leading-none font-semibold text-slate-700 dark:text-slate-300 w-full truncate">
                      {colorNames[col.name][language]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 7: Silkscreen */}
          <div className="flex flex-col md:flex-row py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4 pb-3 mb-2">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0 pt-1`}>
              {renderLabel("silkscreen")}
            </div>
            <div className="flex-1 select-none">
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 bg-slate-50 dark:bg-slate-900/60 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700/80">
                {[
                  { name: "White", class: "bg-white border border-slate-300 dark:border-slate-600" },
                  { name: "Black", class: "bg-slate-950" },
                  { name: "Yellow", class: "bg-[#fbbf24]" },
                  { name: "Blue", class: "bg-blue-600" },
                  { name: "Grey", class: "bg-slate-400" },
                  { name: "None", class: "" }
                ].map((silk) => {
                  const allowed = isSilkscreenAllowed(color, silk.name);
                  return (
                    <button
                      key={silk.name}
                      type="button"
                      disabled={!allowed}
                      title={silkscreenNames[silk.name][language]}
                      onClick={() => setSilkscreen(silk.name)}
                      className={`group flex flex-col items-center justify-center gap-1.5 p-1 rounded-xl border transition-all duration-200 min-h-[48px] relative select-none ${
                        !allowed
                          ? "opacity-25 cursor-not-allowed pointer-events-none border-dashed border-slate-200 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-900 text-slate-400"
                          : silkscreen === silk.name
                          ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold shadow-sm cursor-pointer"
                          : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all cursor-pointer"
                      }`}
                    >
                      {/* Color Rectangle */}
                      <div className="relative w-10 h-5 md:w-11 md:h-5 rounded flex items-center justify-center overflow-hidden shrink-0 shadow-sm border border-slate-200/60 dark:border-slate-700/60">
                        {silk.name === "None" ? (
                          <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <div className="w-[140%] h-[2px] bg-red-500 rotate-45 transform origin-center"></div>
                          </div>
                        ) : (
                          <div className={`absolute inset-0 ${silk.class}`}></div>
                        )}

                        {silkscreen === silk.name && (
                          <span className={`block w-1.5 h-1.5 rounded-full z-10 ${
                            silk.name === "White" || silk.name === "Yellow" ? "bg-black" : "bg-white"
                          }`}></span>
                        )}
                      </div>

                      {/* Display Label */}
                      <span className="text-[10px] text-center leading-none font-semibold text-slate-700 dark:text-slate-300 w-full truncate">
                        {silkscreenNames[silk.name][language]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Row 7.2: Outer Copper Weight */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-44" : "md:w-52"} shrink-0`}>
              {renderLabel("copperWeight")}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-1.5 max-w-2xl font-sans text-xs">
                {["0.5OZ (18um)", "1OZ (35um)", "2OZ (70um)", "3OZ (105um)", "4OZ (140um)", "5OZ (175um)"].map((wt) => (
                  <button
                    key={wt}
                    type="button"
                    onClick={() => setCopperWeight(wt)}
                    className={`py-1.5 px-3 rounded-xl border transition-all cursor-pointer ${
                      copperWeight === wt
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                    }`}
                  >
                    {wt}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 7.3: Inner Copper Weight */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-44" : "md:w-52"} shrink-0`}>
              {renderLabel("innerCopperWeight")}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-1.5 max-w-xl font-sans text-xs">
                {["0.5OZ (18um)", "1OZ (35um)", "2OZ (70um)"].map((wt) => (
                  <button
                    key={wt}
                    type="button"
                    onClick={() => setInnerCopperWeight(wt)}
                    className={`py-1.5 px-3 rounded-xl border transition-all cursor-pointer ${
                      innerCopperWeight === wt
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                    }`}
                  >
                    {wt}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 7.5: Substrate Grade (TG) */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("materialType")}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-1.5 max-w-xl font-sans text-xs">
                {["FR4 TG135", "FR4 TG140", "FR4 TG150", "FR4 TG155", "FR4 TG170", "HIGH-CTI"].map((mt) => (
                  <button
                    key={mt}
                    type="button"
                    onClick={() => setMaterialType(mt)}
                    className={`py-1.5 px-3.5 text-xs rounded-xl border transition-all cursor-pointer ${
                      materialType === mt
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold scale-105"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all cursor-pointer"
                    }`}
                  >
                    {mt}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 7.7: Surface Finish */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("surfaceFinish")}
            </div>
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row gap-2 max-w-xl">
                {[
                  { key: "HASL (with lead)", fLabel: language === "en" ? "HASL with Lead" : "قلع معمولی با سرب (HASL)" },
                  { key: "LeadFree HASL", fLabel: language === "en" ? "Lead-Free HASL (LF)" : "قلع بدون سرب (HASL-LF)" },
                  { key: "ENIG", fLabel: language === "en" ? "ENIG (Gold Plating)" : "آبکاری با طلا (ENIG)" }
                ].map((finishOption) => (
                  <button
                    key={finishOption.key}
                    type="button"
                    onClick={() => setSurfaceFinish(finishOption.key)}
                    className={`flex-1 py-2 px-3 text-xs rounded-xl border transition-all cursor-pointer ${
                      surfaceFinish === finishOption.key
                        ? "border-blue-600 bg-blue-50/60 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                    }`}
                  >
                    <span className="block font-sans font-bold">{finishOption.fLabel}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 8: Thickness */}
          <div className="flex flex-col md:flex-row md:items-center py-2 last:border-0 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-36" : "md:w-52"} shrink-0`}>
              {renderLabel("thickness")}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap gap-1.5 font-mono">
                  {[0.2, 0.4, 0.6, 0.8, 1.0, 1.2, 1.6, 2.0, 2.4, 2.8, 3.2, 3.6, 4.0, 4.4].map((th) => (
                    <button
                      key={th}
                      type="button"
                      onClick={() => setThickness(th)}
                      className={`py-1.5 px-3.5 text-xs rounded-xl border transition-all cursor-pointer ${
                        thickness === th
                          ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold scale-105"
                          : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                      }`}
                    >
                      {th}
                    </button>
                  ))}
                </div>
                <span className="text-sm font-semibold text-slate-500 dark:text-slate-400 shrink-0">
                  {language === "fa" ? "میلی‌متر" : "mm"}
                </span>
              </div>
            </div>
          </div>

        </div>
        {/* High Spec Options - AS REQUESTED IN IMAGE 2 */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-1">
          <div className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-700/40 pb-4 mb-2">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-950/40 rounded-lg text-indigo-600 dark:text-indigo-400 shrink-0">
              <Sliders size={22} />
            </div>
            <div>
              <h3 className="font-bold text-base text-slate-800 dark:text-white">
                {t.highSpecs[language]}
              </h3>
              <p className="text-xs text-slate-400 dark:text-slate-400">
                {t.highSpecsDesc[language]}
              </p>
            </div>
          </div>



          {/* Row 2: Via Covering */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-44" : "md:w-52"} shrink-0`}>
              {renderLabel("viaCovering")}
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 max-w-2xl text-xs font-semibold">
                {[
                  { key: "Tented", fLabel: language === "en" ? "Tented Solder Cover" : "رنگ محافظ اصلی" },
                  { key: "Untented", fLabel: language === "en" ? "Untented Exposed" : "باز بودن محافظ اصلی" },
                  { key: "Plugged", fLabel: language === "en" ? "Plugged Metal Via" : "پر شده با فلز" },
                  { key: "Epoxy Filled & Capped", fLabel: language === "en" ? "Epoxy Filled Capped" : "پر شده با اپوکسی" }
                ].map((cov) => (
                  <button
                    key={cov.key}
                    type="button"
                    onClick={() => setViaCovering(cov.key)}
                    className={`py-1.5 rounded-xl border transition-all cursor-pointer ${
                      viaCovering === cov.key
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                    }`}
                  >
                    {cov.fLabel}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 3: Via Plating Method */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-44" : "md:w-52"} shrink-0`}>
              {renderLabel("viaPlating")}
            </div>
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row gap-2 max-w-2xl text-xs">
                {[
                  { key: "Not Specified", fLabel: language === "en" ? "Not Specified" : "بدون تعیین نوع مستقیم" },
                  { key: "Conductive Adhesive", fLabel: language === "en" ? "Conductive Adhesive" : "چسب رسانا (انتقال حرارتی)" },
                  { key: "Horizontal Electroless Copper Plating", fLabel: language === "en" ? "Horizontal Electroless" : "آبکاری افقی الکترولس" }
                ].map((plat) => (
                  <button
                    key={plat.key}
                    type="button"
                    onClick={() => setViaPlating(plat.key)}
                    className={`flex-1 py-1.5 px-3 rounded-xl border transition-all cursor-pointer ${
                      viaPlating === plat.key
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold text-center"
                        : `border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all ${isRTL ? "text-right font-sans" : "text-left font-sans"}`
                    }`}
                  >
                    <span className="block font-medium">{plat.fLabel}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 4: Min via size/diameter */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-44" : "md:w-52"} shrink-0`}>
              {renderLabel("minViaSize")}
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 max-w-2xl text-xs font-semibold">
                {[
                  { key: "0.15mm", display: "0.15mm" },
                  { key: "0.2mm", display: "0.2mm" },
                  { key: "0.25mm", display: "0.25mm" },
                  { key: "0.3mm", display: "0.3mm" },
                  { key: "No Vias", display: language === "en" ? "No Vias" : "بدون سوراخ" }
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setMinViaSize(item.key)}
                    className={`py-1.5 rounded-xl border transition-all cursor-pointer ${
                      minViaSize === item.key
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                    }`}
                  >
                    {item.display}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 5: Board Outline Tolerance */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-44" : "md:w-52"} shrink-0`}>
              {renderLabel("outlineTolerance")}
            </div>
            <div className="flex-1">
              <div className="flex gap-2 max-w-md text-xs">
                {[
                  { key: "±0.2mm(Regular)", fLabel: language === "en" ? "Regular ±0.2mm" : "معمولی ۰.۲± میلیمتر" },
                  { key: "±0.1mm(Precision)", fLabel: language === "en" ? "Precision ±0.1mm" : "دقیق ۰.۱± (دقت بالا)" }
                ].map((tol) => (
                  <button
                    key={tol.key}
                    type="button"
                    onClick={() => setOutlineTolerance(tol.key)}
                    className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${
                      outlineTolerance === tol.key
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                    }`}
                  >
                    {tol.fLabel}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 6: Confirm Production file */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-44" : "md:w-52"} shrink-0`}>
              {renderLabel("confirmFile")}
            </div>
            <div className="flex-1">
              <div className="flex gap-2 max-w-md text-xs">
                {[
                  { key: "No", fLabel: language === "en" ? "No (Direct Build)" : "خیر (تولید مستقیم)" },
                  { key: "Yes", fLabel: language === "en" ? "Yes (Blueprint Review)" : "بله (پیش نمایش فایل)" }
                ].map((cf) => (
                  <button
                    key={cf.key}
                    type="button"
                    onClick={() => setConfirmFile(cf.key)}
                    className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${
                      confirmFile === cf.key
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                    }`}
                  >
                    {cf.fLabel}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Toggle Switches for Edge Metals, Castellated, Gold Fingers, Blind cuts */}
          <div className="border-b border-slate-100 dark:border-slate-700/40 py-2.5 grid grid-cols-2 md:grid-cols-4 gap-3">
            
            {/* Gold Fingers */}
            <div className="bg-slate-50 dark:bg-slate-900/40 p-3 rounded-xl border border-slate-100 dark:border-slate-800/80 space-y-2">
              <div className="flex justify-between items-center text-xs font-semibold">
                {renderLabel("goldFingers")}
              </div>
              <div className="flex gap-1 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setGoldFingers(false)}
                  className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${!goldFingers ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"}`}
                >
                  {language === "en" ? "No" : "خیر"}
                </button>
                <button
                  type="button"
                  onClick={() => setGoldFingers(true)}
                  className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${goldFingers ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"}`}
                >
                  {language === "en" ? "Yes" : "بله"}
                </button>
              </div>
            </div>

            {/* Castellated Holes */}
            <div className="bg-slate-50 dark:bg-slate-900/40 p-3 rounded-xl border border-slate-100 dark:border-slate-800/80 space-y-2">
              <div className="flex justify-between items-center text-xs font-semibold">
                {renderLabel("castellatedHoles")}
              </div>
              <div className="flex gap-1 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setCastellatedHoles(false)}
                  className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${!castellatedHoles ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"}`}
                >
                  {language === "en" ? "No" : "خیر"}
                </button>
                <button
                  type="button"
                  onClick={() => setCastellatedHoles(true)}
                  className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${castellatedHoles ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"}`}
                >
                  {language === "en" ? "Yes" : "بله"}
                </button>
              </div>
            </div>

            {/* Edge Plating */}
            <div className="bg-slate-50 dark:bg-slate-900/40 p-3 rounded-xl border border-slate-100 dark:border-slate-800/80 space-y-2">
              <div className="flex justify-between items-center text-xs font-semibold">
                {renderLabel("edgePlating")}
              </div>
              <div className="flex gap-1 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setEdgePlating(false)}
                  className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${!edgePlating ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"}`}
                >
                  {language === "en" ? "No" : "خیر"}
                </button>
                <button
                  type="button"
                  onClick={() => setEdgePlating(true)}
                  className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${edgePlating ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"}`}
                >
                  {language === "en" ? "Yes" : "بله"}
                </button>
              </div>
            </div>

            {/* Blind Slots */}
            <div className="bg-slate-50 dark:bg-slate-900/40 p-3 rounded-xl border border-slate-100 dark:border-slate-800/80 space-y-2">
              <div className="flex justify-between items-center text-xs font-semibold">
                {renderLabel("blindSlots")}
              </div>
              <div className="flex gap-1 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setBlindSlots(false)}
                  className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${!blindSlots ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"}`}
                >
                  {language === "en" ? "No" : "خیر"}
                </button>
                <button
                  type="button"
                  onClick={() => setBlindSlots(true)}
                  className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${blindSlots ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"}`}
                >
                  {language === "en" ? "Yes" : "بله"}
                </button>
              </div>
            </div>

          </div>

          {/* Mark on PCB */}
          <div className="flex flex-col md:flex-row md:items-center py-2 border-b border-slate-100 dark:border-slate-700/40 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-44" : "md:w-52"} shrink-0`}>
              {renderLabel("markOnPcb")}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-1.5 max-w-xl text-xs font-semibold">
                {[
                  { key: "Remove Mark", fLabel: language === "en" ? "Remove Mark" : "حذف لوگو" },
                  { key: "No", fLabel: language === "en" ? "No (Keep Mark)" : "چاپ معمولی" },
                  { key: "2D barcode (Serial Number)", fLabel: language === "en" ? "2D Barcode" : "بارکد دوبعدی" },
                  { key: "Production Date", fLabel: language === "en" ? "Production Date (Week/Year)" : "تاریخ تولید (هفته/سال)" }
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setMarkOnPcb(item.key)}
                    className={`py-1.5 px-3 rounded-xl border transition-all cursor-pointer ${
                      markOnPcb === item.key
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                    }`}
                  >
                    {item.fLabel}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Humidity Card */}
          <div className="flex flex-col md:flex-row md:items-center py-2 last:border-0 gap-1.5 md:gap-4">
            <div className={`w-full ${isRTL ? "md:w-60" : "md:w-52"} shrink-0`}>
              {renderLabel("humidityCard")}
            </div>
            <div className="flex-1">
              <div className="flex gap-1 max-w-[140px] text-xs font-semibold">
                {[
                  { key: "false", val: false, fLabel: language === "en" ? "No" : "خیر" },
                  { key: "true", val: true, fLabel: language === "en" ? "Yes" : "بله" }
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setHumidityCard(item.val)}
                    className={`flex-1 py-1.5 rounded-xl border transition-all cursor-pointer ${
                      humidityCard === item.val
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 font-bold"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-slate-400 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-800 transition-all"
                    }`}
                  >
                    {item.fLabel}
                  </button>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* Gerber File Upload & Special Remarks Card */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Upload Gerber Box */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-4 flex flex-col justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm text-slate-700 dark:text-slate-200">
                  {language === "en" ? "Gerber File" : "فایل ضمیمه"}
                </span>
                <span className="text-[10px] text-rose-500 font-bold bg-rose-50 dark:bg-rose-950/40 py-0.5 px-2 rounded-full font-sans">الزامی</span>
              </div>
              {language === "en" && (
                <p className="text-xs text-slate-400">ZIP or RAR files containing copper, solder, silkscreen, and outlines.</p>
              )}
            </div>

            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-5 text-center transition-all cursor-pointer flex flex-col items-center justify-center min-h-[140px] ${
                uploadedFile 
                  ? "border-emerald-500/70 bg-emerald-50/10 dark:bg-emerald-950/5 text-emerald-600 dark:text-emerald-400"
                  : "border-slate-200 dark:border-slate-700 hover:border-slate-400 text-slate-400"
              }`}
            >
              <input 
                type="file" 
                accept=".zip,.rar,.7z"
                onChange={handleFileChange}
                className="hidden" 
                id="gerber-file-input"
              />
              
              {isUploading ? (
                <div className="space-y-2 py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-xs text-blue-600">در حال آنالیز و آپلود فایل گربر...</p>
                </div>
              ) : uploadedFile ? (
                <label htmlFor="gerber-file-input" className="cursor-pointer space-y-2 py-2 w-full">
                  <FileCheck size={32} className="mx-auto text-emerald-500 animate-bounce" />
                  <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate max-w-[280px] mx-auto font-mono">{uploadedFile.name}</p>
                  <p className="text-[10px] text-slate-400">حجم فایل: {uploadedFile.size} - برای ویرایش کلیک کنید</p>
                </label>
              ) : (
                <label htmlFor="gerber-file-input" className="cursor-pointer space-y-2 py-3 w-full">
                  <UploadCloud size={32} className="mx-auto text-slate-400" />
                  <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    {language === "en" ? "Drag & drop Gerber file (or click)" : "کشیدن و رها کردن فایل (یا کلیک کنید)"}
                  </p>
                  <p className="text-[10px] text-slate-400 font-mono">
                    {language === "en" ? "Allowed files: ZIP / RAR" : "فایل‌های مجاز zip/rar"}
                  </p>
                </label>
              )}
            </div>
            {uploadError && (
              <div className="flex items-start gap-1 p-2 bg-rose-50 dark:bg-rose-950/10 text-rose-600 dark:text-rose-400 rounded-lg text-[10px] leading-relaxed">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{uploadError}</span>
              </div>
            )}
          </div>

          {/* Detailed Specification remark */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-2 flex flex-col justify-between">
            <label className="font-bold text-sm text-slate-700 dark:text-slate-200 block">
              {language === "en" ? "Special Remarks" : "توضیحات ویژه"}
            </label>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder={language === "en" ? "Please fill in the PCB details so we can understand your requirements as clearly as possible." : "جزئیات PCB را پر کنید تا ما بتوانیم نیازهای شما را تا حد امکان واضح درک کنیم."}
              className="w-full flex-1 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 text-xs focus:outline-none focus:ring-1 focus:ring-slate-500 resize-none font-sans leading-relaxed min-h-[75px]"
            />
          </div>

        </div>

        {/* Awaiting File Upload Container - Outside the Receipt Box */}
        {!uploadedFile && (
          <div className="border border-dashed border-slate-350 dark:border-slate-800 rounded-3xl p-8 bg-slate-50 dark:bg-slate-950/25 text-center space-y-3.5 min-h-[165px] flex flex-col justify-center items-center font-sans text-right" dir="rtl">
            <div className="p-3 bg-slate-100 dark:bg-slate-950/55 rounded-full text-slate-500 dark:text-slate-600 shadow-sm border border-slate-200/50 dark:border-slate-800/40">
              <UploadCloud size={24} className="animate-pulse text-blue-500 dark:text-blue-400" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-bold text-slate-700 dark:text-slate-300">
                {language === "en" 
                  ? "Awaiting Custom Gerber Package Attachment..." 
                  : "در انتظار بارگزاری فایل ضمیه..."}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xl leading-relaxed mx-auto font-sans leading-relaxed">
                {language === "en"
                  ? "Once you upload a .ZIP / .RAR file, the interactive CAM terminal and file extraction inspector will initiate here."
                  : "به محض بارگزاری فایل فشرده PCB، شبیه سازی خودکار مدارک فنی فعال و فایل های مناسب تولید در زیر نمایان میشود."}
              </p>
            </div>
          </div>
        )}

        {/* Dynamic Quote Receipt Block & Submit Button */}
        <div className="bg-slate-900 text-white rounded-3xl border border-slate-800 shadow-xl overflow-hidden relative p-6 sm:p-8 space-y-6">
          
          {/* Aesthetic decorative pattern */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="flex flex-col gap-6 w-full">
            
            {/* Quick Summary & Breakdown or Files list */}
            <div className="w-full space-y-4 flex flex-col justify-between">
              
              {/* Enriched specifications summary grid - complete technical sheet */}
              {/* TEMPORARILY HIDDEN BY USER REQUEST
              <div className="bg-slate-950/75 p-5 rounded-2xl border border-slate-800 relative overflow-hidden font-sans space-y-1" dir={language === "en" ? "ltr" : "rtl"}>
                <div className="absolute top-0 right-0 left-0 h-[2px] bg-gradient-to-l from-emerald-500 via-blue-500 to-indigo-500 opacity-80"></div>
                
                <div className="divide-y divide-slate-800/60 font-sans">
                  <div className="flex justify-between items-center py-2.5 text-xs sm:text-sm">
                    <span className="text-slate-400 font-medium font-sans">
                      {language === "en" ? "Layer Count:" : "تعداد لایه:"}
                    </span>
                    <span className="font-bold text-slate-200 font-sans">
                      {language === "en" ? `${layers} Layers` : `${layers} لایه`}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2.5 text-xs sm:text-sm">
                    <span className="text-slate-400 font-medium font-sans">
                      {language === "en" ? "Material & TG Type:" : "متریال و نوع آن:"}
                    </span>
                    <span className="font-bold text-slate-200 font-sans">
                      {baseMaterial} / {materialType}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2.5 text-xs sm:text-sm">
                    <span className="text-slate-400 font-medium font-sans">
                      {language === "en" ? "Solder Mask / Silkscreen:" : "رنگ برد و رنگ چاپ راهنما:"}
                    </span>
                    <span className="font-bold text-slate-200 font-sans">
                      {color} / {silkscreen}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2.5 text-xs sm:text-sm">
                    <span className="text-slate-400 font-medium font-sans">
                      {language === "en" ? "Copper Thickness:" : "ضخامت مس:"}
                    </span>
                    <span className="font-bold text-slate-200 font-sans" dir="ltr">
                      {copperWeight}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2.5 text-xs sm:text-sm">
                    <span className="text-slate-400 font-medium font-sans">
                      {language === "en" ? "PCB Thickness:" : "ضخامت برد:"}
                    </span>
                    <span className="font-bold text-slate-200 font-sans" dir="ltr">
                      {thickness} mm
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2.5 text-xs sm:text-sm">
                    <span className="text-slate-400 font-medium font-sans">
                      {language === "en" ? "Surface Finish:" : "مدل آبکاری:"}
                    </span>
                    <span className="font-bold text-slate-200 font-sans">
                      {surfaceFinish}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2.5 text-xs sm:text-sm">
                    <span className="text-slate-400 font-medium font-sans">
                      {language === "en" ? "Quantity:" : "تعداد:"}
                    </span>
                    <span className="font-bold text-slate-200 font-sans">
                      {language === "en" ? `${qty} PCS` : `${qty} عدد`}
                    </span>
                  </div>
                </div>
              </div>
              */}

              {uploadedFile && (
                <div className="bg-white dark:bg-slate-950/55 p-4 sm:p-5 rounded-2xl border border-slate-200 dark:border-slate-800/85 space-y-4 flex flex-col justify-between" dir="ltr">
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900/60 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800/40" dir={language === "en" ? "ltr" : "rtl"}>
                    <div className="flex items-center gap-2">
                      <div className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </div>
                      <span className="text-[11.5px] font-bold text-slate-700 dark:text-slate-300 font-sans">
                        {language === "en" 
                          ? `Identified Files (${filesToShow.length} files)` 
                          : `فایل های شناسایی شده (${filesToShow.length} فایل)`}
                      </span>
                    </div>
                  </div>

                  {/* horizontal or grid list of files */}
                  <div className="flex flex-col gap-1 w-full">
                    {filesToShow.map((file) => {
                      const isGerber = file.type === "gerber";
                      const isWord = file.type === "word";
                      const isText = file.type === "text";
                      const isImage = file.type === "image";
                      const isExcel = file.type === "excel";

                      let iconColor = "text-slate-500 dark:text-slate-400";
                      let sizeSpanClass = "text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700";

                      if (isGerber) {
                        iconColor = "text-emerald-600 dark:text-emerald-500";
                        sizeSpanClass = "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/45 border border-emerald-200 dark:border-emerald-500/20";
                      } else if (isWord) {
                        iconColor = "text-blue-600 dark:text-blue-500";
                        sizeSpanClass = "text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/45 border border-blue-200 dark:border-blue-500/20";
                      } else if (isText) {
                        iconColor = "text-slate-500 dark:text-slate-400";
                      } else if (isImage) {
                        iconColor = "text-purple-600 dark:text-purple-500";
                        sizeSpanClass = "text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/45 border border-purple-200 dark:border-purple-500/20";
                      } else if (isExcel) {
                        iconColor = "text-teal-600 dark:text-teal-500";
                        sizeSpanClass = "text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-950/45 border border-teal-200 dark:border-teal-500/20";
                      }

                      const extUpper = file.name.substring(file.name.lastIndexOf(".") + 1).toUpperCase();
                      const extDef = recognizedExtensionsMap[extUpper];
                      
                      let meaning = "";
                      let meaningFa = "";
                      const lowerName = file.name.toLowerCase();
                      if (lowerName.endsWith(".txt") && (lowerName.includes("readme") || lowerName.includes("guide") || lowerName.includes("manual"))) {
                        meaning = "Project Readme / Instructions Document";
                        meaningFa = "سند راهنمای پروژه";
                      } else if (lowerName.endsWith(".xlsx") || lowerName.endsWith(".xls")) {
                        meaning = "Bill of Materials (BOM)";
                        meaningFa = "لیست قطعات (BOM)";
                      } else if (lowerName.includes("3d") || ["png", "jpg", "jpeg", "gif", "bmp"].some(imgExt => lowerName.endsWith("." + imgExt))) {
                        meaning = "3D CAD Model / Aspect Preview";
                        meaningFa = "مدل سه بعدی / پیش نمایش ظاهری";
                      } else if (lowerName.endsWith(".docx") || lowerName.endsWith(".doc") || lowerName.endsWith(".pdf")) {
                        meaning = "Technical Specs & Stackup Details";
                        meaningFa = "مشخصات فنی و چیدمان لایه‌ها";
                      } else if (extDef) {
                        meaning = extDef.altiumMeaning || extDef.name.en;
                        meaningFa = extDef.name.fa;
                      } else {
                        meaning = "Altium Custom CAD Layer";
                        meaningFa = "لایه سفارشی آلتیوم";
                      }

                      const truncatedName = shortenFileName(file.name, 28);

                      return (
                        <div
                          key={file.name}
                          className="w-full py-1.5 text-left flex items-center justify-between gap-3 group relative"
                        >
                          <div className="flex items-center gap-3 w-full justify-between" dir="ltr">
                            <div className="flex items-center gap-2 min-w-0 flex-1 justify-start">
                              <div className={`flex-shrink-0 ${iconColor} transition-transform group-hover:scale-110`}>
                                {isGerber && <CircuitBoard size={16} />}
                                {isWord && (
                                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3.5 14H11V15h4.5v2zm2-4H11V11h6.5v2zm0-4H11V7h6.5v2zM9 7v10H6.5V7H9z"/>
                                  </svg>
                                )}
                                {isText && (
                                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                                  </svg>
                                )}
                                {isImage && (
                                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                    <path d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
                                  </svg>
                                )}
                                {isExcel && (
                                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z"/>
                                  </svg>
                                )}
                              </div>
                              <div className="text-left min-w-0 flex-1 flex items-center justify-between gap-3" dir="ltr">
                                <div className="min-w-0 flex-1 flex flex-row items-center gap-2.5 flex-wrap">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedViewerFile(file);
                                      setModalViewMode(file.type === "gerber" ? "draw" : "text");
                                    }}
                                    title={file.name}
                                    className="cursor-pointer font-mono text-[13px] text-slate-800 dark:text-slate-100 font-bold truncate transition-all duration-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:-translate-y-0.5 active:scale-95 leading-none focus:outline-none relative after:absolute after:bottom-[-2px] after:left-0 after:h-[2px] after:w-0 hover:after:w-full after:bg-indigo-500 after:transition-all after:duration-300"
                                  >
                                    {truncatedName}
                                  </button>
                                  <div className="flex items-center gap-1.5 flex-wrap mt-[2px]">
                                    <span className="text-slate-600 dark:text-slate-300 font-sans font-medium text-[11px] sm:text-[11.5px] select-none bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800/80 px-1.5 py-0.5 rounded-md flex-shrink-0 leading-none shadow-sm">
                                      {`[ ${meaningFa.replace(/\s*\([^)]*\)/g, "").trim()} ]`}
                                    </span>
                                    <span className="text-slate-500 dark:text-slate-500/70 font-mono text-[9.5px] sm:text-[10px] select-none leading-none">
                                      {`{ ${meaning} }`}
                                    </span>
                                  </div>
                                </div>
                                <span className={`font-mono font-bold text-[11px] px-2 py-0.5 rounded-md flex-shrink-0 select-none shadow-sm ${sizeSpanClass}`}>
                                  [{file.size}]
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div> {/* This closes quick summary container */}

            {/* Price & Submit Order Component */}
            <div className="w-full bg-slate-950 p-6 sm:p-8 rounded-3xl border border-slate-800 relative overflow-hidden" dir="rtl">
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none"></div>
              
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                
                {/* Right / Top Block in RTL: Details grid (col-span-12 or col-span-7) */}
                <div className="lg:col-span-7 space-y-4">
                  <span className="text-[11px] uppercase font-extrabold tracking-wider text-slate-400 block font-sans">
                    برآورد زنده و تفکیک شده هزینه ساخت فیزیکی
                  </span>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-slate-900/30 p-3.5 rounded-2xl border border-slate-800/40 flex justify-between items-center sm:flex-col sm:items-start sm:justify-center gap-1.5 min-h-[75px]">
                      <span className="text-slate-400 text-xs font-sans">هزینه شابلون و آماده‌سازی:</span>
                      <span className="font-mono text-slate-100 font-bold text-sm">۲۵۰,۰۰۰ تومان</span>
                    </div>

                    <div className="bg-slate-900/30 p-3.5 rounded-2xl border border-slate-800/40 flex justify-between items-center sm:flex-col sm:items-start sm:justify-center gap-1.5 min-h-[75px]">
                      <span className="text-slate-400 text-xs font-sans">تولید پنل لایه‌ها ({layers} لایه):</span>
                      <span className="font-mono text-slate-100 font-bold text-sm">
                        {(Math.round(((Number(width) || 10) * (Number(height) || 10) * (Number(qty) || 5) * (Number(layers) || 2) * 85) / 1000) * 1000).toLocaleString('fa-IR')} تومان
                      </span>
                    </div>

                    <div className="bg-slate-900/30 p-3.5 rounded-2xl border border-slate-800/40 flex justify-between items-center sm:flex-col sm:items-start sm:justify-center gap-1.5 min-h-[75px]">
                      <span className="text-slate-400 text-xs font-sans">آبکاری پدها ({surfaceFinish}):</span>
                      <span className="font-mono text-slate-100 font-bold text-sm">{surfaceFinish}</span>
                    </div>

                    <div className="bg-slate-900/30 p-3.5 rounded-2xl border border-slate-800/40 flex justify-between items-center sm:flex-col sm:items-start sm:justify-center gap-1.5 min-h-[75px]">
                      <span className="text-slate-400 text-xs font-sans">ضمانت بررسی مدارک (تضمین ساخت):</span>
                      <span className="text-emerald-400 font-bold text-xs sm:text-sm font-sans">رایگان و اتوماتیک</span>
                    </div>
                  </div>
                </div>

                {/* Left / Bottom Block in RTL: Summary box & submit button (col-span-12 or col-span-5) */}
                <div className="lg:col-span-5 flex flex-col justify-center space-y-4 lg:border-r lg:border-slate-800/70 lg:pr-8">
                  <div className="bg-gradient-to-br from-slate-900/70 to-slate-950 p-4.5 rounded-2xl border border-slate-800/80 text-center space-y-1">
                    <span className="text-xs text-slate-400 block font-sans">جمع کل برآورد هزینه ساخت:</span>
                    <span className="text-3xl font-black text-emerald-400 block font-mono">
                      {(250000 + Math.round(((Number(width) || 10) * (Number(height) || 10) * (Number(qty) || 5) * (Number(layers) || 2) * 85) / 1000) * 1000).toLocaleString('fa-IR')}{" "}
                      <span className="text-xs font-semibold text-slate-350 font-sans">تومان</span>
                    </span>
                  </div>

                  <div className="space-y-2">
                    <button
                      type="submit"
                      className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-sans font-bold text-sm py-4 px-6 rounded-2xl transition-all duration-300 shadow-lg shadow-emerald-500/10 hover:shadow-emerald-500/25 transform hover:-translate-y-0.5 active:translate-y-0 active:scale-98 flex items-center justify-center gap-2 cursor-pointer group"
                    >
                      <CircuitBoard size={16} className="group-hover:rotate-12 transition-transform" />
                      <span>ثبت نهایی و ذخیره سفارش</span>
                    </button>
                    <p className="text-[9.5px] text-slate-500 leading-normal text-center font-sans">
                      با ثبت نهایی، مشخصات فنی در پنل مدیریت پروژه ذخیره شده و پس از اعتبارسنجی اتوماتیک Gerber خط تولید آغاز می‌شود.
                    </p>
                  </div>
                </div>

              </div>
            </div>

          </div> {/* This closes flex-col gap-6 w-full */}
        </div> {/* This closes Dynamic Quote Receipt Block div */}
                  <AnimatePresence>
                    {selectedViewerFile && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
                        <motion.div 
                          className="w-full max-w-6xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                          initial={{ scale: 0.95, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.95, opacity: 0 }}
                        >
                          {/* Modal Window Header */}
                          <div className="bg-slate-950 px-6 py-4 flex items-center justify-between border-b border-slate-800/85">
                            <button
                              type="button"
                              onClick={() => setSelectedViewerFile(null)}
                              className="text-slate-400 hover:text-white p-1 rounded-full hover:bg-slate-900 transition-colors"
                            >
                              <X size={18} />
                            </button>

                            {/* Always in Drawing / Gerber rendering mode */}
                            <div></div>

                            <div className="flex items-center gap-3">
                              <h3 className="text-sm font-black text-slate-100 flex items-center gap-2">
                                {selectedViewerFile.name}
                                <CircuitBoard size={16} className="text-emerald-500" />
                              </h3>
                            </div>
                          </div>

                          {/* Modal Scrollable Body */}
                          <div className="p-6 overflow-y-auto flex-1">
                            {/* Gerber File Details Drawing Layout mode */}
                            {selectedViewerFile.type === "gerber" && modalViewMode === "draw" && (
                              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch animate-fadeIn">
                                {/* Controls & Layers inspector column (Toolbox is ALWAYS here!) */}
                                <div className="lg:col-span-5 space-y-4 text-right" dir="rtl">
                                  {/* Technical Parameters Card */}
                                  <div className="bg-slate-950 p-5 rounded-xl border border-slate-800 space-y-4">
                                    <span className="text-xs font-bold text-emerald-400 block font-sans">
                                      {selectedViewerFile.name.endsWith(".drr") || selectedViewerFile.name.endsWith(".txt") 
                                        ? "مشخصات فنی فایل سوراخ‌کاری NC" 
                                        : "پارامترهای فنی نقشه گربر (Gerber PCB)"}
                                    </span>
                                    
                                    <div className="space-y-2.5 text-xs text-slate-350">
                                      <div className="flex justify-between border-b border-slate-900 pb-2">
                                        <span className="text-slate-500 font-sans">نام سند پرونده:</span>
                                        <span className="font-mono text-slate-200 text-left" dir="ltr">{selectedViewerFile.name}</span>
                                      </div>
                                      <div className="flex justify-between border-b border-slate-900 pb-2">
                                        <span className="text-slate-500 font-sans">طرح عایق / لایه مربوطه:</span>
                                        <span className="text-slate-200 text-left font-bold font-sans">
                                          {modalExtDef ? modalExtDef.name.fa : 'لایه اختصاصی کاربر'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between border-b border-slate-900 pb-2">
                                        <span className="text-slate-500 font-sans">حجم فایل:</span>
                                        <span className="font-mono text-slate-300 text-left" dir="ltr">{selectedViewerFile.size}</span>
                                      </div>
                                      <div className="flex justify-between border-b border-slate-900 pb-2">
                                        <span className="text-slate-500 font-sans">نوع داده و فرمت:</span>
                                        <span className="text-emerald-400 font-mono text-left font-bold text-[10.5px]" dir="ltr">
                                          {selectedViewerFile.name.endsWith(".drr") || selectedViewerFile.name.endsWith(".txt") 
                                            ? "Excellon NC Drill / ASCII" 
                                            : "Gerber (RS-274X, X2, X3) / ASCII vector data"}
                                        </span>
                                      </div>
                                      <div className="flex justify-between border-b border-slate-900 pb-2">
                                        <span className="text-slate-500 font-sans">محدوده فیزیکی مدار:</span>
                                        <span className="font-mono text-slate-300 text-left text-[11px]" dir="ltr">
                                          {boardBounds ? `${boardBounds.w.toFixed(2)} × ${boardBounds.h.toFixed(2)} mm` : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between pt-1">
                                        <span className="text-slate-500 font-sans">سیستم مختصات و صفرها:</span>
                                        <span className="text-emerald-400 font-sans text-left text-[11px] font-bold">
                                          شناسایی خودکار هوشمند (Auto)
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Vector Gerber CAD canvas viewport column - ACTUAL PARSED ELEMENTS */}
                                <div className="lg:col-span-7 flex flex-col gap-3">
                                  {/* Viewport container */}
                                  <div 
                                    className={`bg-slate-950 rounded-2xl border border-slate-800 relative min-h-[420px] max-h-[500px] w-full flex flex-col items-center justify-center select-none overflow-hidden ${
                                      isDragging ? "cursor-grabbing" : "cursor-grab"
                                    }`}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUpOrLeave}
                                    onMouseLeave={handleMouseUpOrLeave}
                                    onWheel={handleWheel}
                                    onContextMenu={handleContextMenu}
                                  >
                                    {/* Grid background effect */}
                                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#022c22_0.5px,transparent_0.5px),linear-gradient(to_bottom,#022c22_0.5px,transparent_0.5px)] bg-[size:15px_15px] opacity-35 pointer-events-none"></div>

                                    {renderError ? (
                                      <div className="p-6 text-center max-w-sm space-y-3 z-10 text-right" dir="rtl">
                                        <div className="w-12 h-12 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center justify-center mx-auto mb-2">
                                          <AlertCircle size={24} />
                                        </div>
                                        <h4 className="text-sm font-bold text-slate-200 text-center">عدم رندر خودکار نقشه</h4>
                                        <p className="text-xs text-slate-400 leading-relaxed font-sans text-center">{renderError}</p>
                                      </div>
                                    ) : svgRender ? (
                                      <div 
                                        className="w-[95%] h-[95%] relative z-10 flex justify-center items-center pointer-events-none text-[#00ff66] transition-transform duration-200 ease-out animate-fadeIn origin-center"
                                        style={{ transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${overrideScale})` } as any}
                                        dangerouslySetInnerHTML={{ __html: svgRender }} 
                                      />
                                    ) : (
                                      <div className="text-center space-y-3 z-10">
                                        <div className="w-8 h-8 rounded-full border-2 border-emerald-500/25 border-t-emerald-500 animate-spin mx-auto"></div>
                                        <span className="text-[11px] text-slate-450 font-sans block">در حال بارگذاری و رندر نقشه برداری Gerber...</span>
                                      </div>
                                    )}

                                    {/* Context Menu */}
                                    {contextMenu && contextMenu.visible && (
                                      <div 
                                        className="fixed z-50 bg-slate-950 border border-slate-800 rounded-xl shadow-2xl py-1.5 w-48 text-right font-sans divide-y divide-slate-900 border-opacity-70 animate-fadeIn"
                                        style={{ top: contextMenu.y, left: contextMenu.x }}
                                        dir="rtl"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setPanOffset({ x: 0, y: 0 });
                                            setOverrideScale(0.8);
                                            setContextMenu(null);
                                          }}
                                          className="w-full text-right px-4 py-2.5 text-xs text-slate-350 hover:bg-slate-900 hover:text-emerald-400 flex items-center justify-between transition cursor-pointer"
                                        >
                                          <span className="font-sans font-bold">مشاهده کامل طرح</span>
                                          <Maximize2 size={13} className="text-slate-400" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setOverrideScale(prev => Math.min(prev * 1.25, 10.0));
                                            setContextMenu(null);
                                          }}
                                          className="w-full text-right px-4 py-2.5 text-xs text-slate-350 hover:bg-slate-900 hover:text-emerald-400 flex items-center justify-between transition cursor-pointer"
                                        >
                                          <span className="font-sans font-bold">بزرگنمایی</span>
                                          <ZoomIn size={13} className="text-slate-400" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setOverrideScale(prev => Math.max(prev / 1.25, 0.1));
                                            setContextMenu(null);
                                          }}
                                          className="w-full text-right px-4 py-2.5 text-xs text-slate-350 hover:bg-slate-900 hover:text-emerald-400 flex items-center justify-between transition cursor-pointer"
                                        >
                                          <span className="font-sans font-bold">کوچکنمایی</span>
                                          <ZoomOut size={13} className="text-slate-400" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Text/Report File Viewer */}
                            {(selectedViewerFile.type === "text" || modalViewMode === "text") && (
                              <div className="bg-slate-950 rounded-2xl border border-slate-800 p-5 font-mono text-xs text-left text-slate-300 leading-relaxed max-h-[500px] overflow-y-auto scrollbar-thin animate-fadeIn">
                                <div className="text-slate-500 text-[10px] mb-3 pb-2 border-b border-slate-900 flex justify-between select-none font-sans">
                                  <span>{selectedViewerFile.type === "gerber" ? "TECHNICAL DOCUMENT / REPORT READER" : "FILE RAW TEXT READER & CAM CONSOLE"}</span>
                                  <span>LINES: {selectedViewerFile.content.split("\n").length}</span>
                                </div>

                                <div className="space-y-1 bg-slate-950/40 p-2.5 rounded">
                                  {selectedViewerFile.content.split("\n").map((line, idx) => (
                                    <div key={idx} className="flex gap-4 items-start font-mono">
                                      <span className="text-slate-600 select-none w-5 text-right font-sans">{idx + 1}</span>
                                      <span className="text-slate-200 block whitespace-pre-wrap">{line}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Window Footer bar */}
                          <div className="bg-slate-950 px-6 py-3 flex items-center justify-between border-t border-slate-800/80 font-mono text-[10px] text-slate-500">
                            <span className="flex items-center gap-1.5 select-none text-slate-500">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
                              STATUS: READY
                            </span>
                            <span>PCB DIMENSION: {boardBounds ? `${boardBounds.w.toFixed(1)} × ${boardBounds.h.toFixed(1)} mm` : "NOT DETECTED FROM FILE"}</span>
                          </div>
                        </motion.div>
                      </div>
                    )}
                  </AnimatePresence>
                </form>
              </div>
            );
          };

          export default PcbOrderForm;
